import {
  ArrowDown,
  ArrowUp,
  Bot,
  BookOpen,
  Building,
  CheckCircle2,
  Clipboard,
  Copy,
  CornerUpLeft,
  Download,
  FileText,
  Gauge,
  GitBranch,
  Globe,
  CalendarClock,
  LifeBuoy,
  ListFilter,
  Loader2,
  Maximize2,
  Minimize2,
  Package,
  Plug,
  Power,
  RefreshCw,
  Rocket,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Square,
  Users,
  X
} from 'lucide-react';
import { StrictMode, memo, startTransition, useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type FormEvent, type KeyboardEvent, type ReactNode, type SVGProps } from 'react';
import { createRoot } from 'react-dom/client';
import type {
  AgentChatResponse,
  AgentCitation,
  AgentChatToolActivity,
  AgentResultCard,
  AgentToolDebugSummary,
  AgentToolDetail,
  AgentToolRun,
  PortalChatProfileSummary,
  PortalPublicConfig,
  PublicApiCatalogEntry,
  PublicIntegrationArtifact,
  PublicIntegrationKitSummary,
  SessionStatus
} from '../shared/portalTypes';
import { PortalChatMessageMaxCharacters } from '../shared/chatLimits';
import {
  DOCUMENTATION_PAGES,
  getDocumentationPage,
  resolveDocumentationHref,
  type DocumentationPage
} from './docsContent';
import { ChatMarkdown } from './chatMarkdown';
import { ActivityView } from './activityView';
import { renderMarkdown } from './markdown';
import { McpToolsView } from './mcpToolsView';
import { JobsView } from './jobsView';
import {
  HTTP_METHODS,
  getParameterExample,
  getPreferredRequestBody,
  getSchemaTypeLabel,
  loadApiOperations,
  type ApiOperation,
  type OpenApiParameter
} from './openApiExplorer';
import mcpPromptCatalog from '../../../agents/iasset-dev-mcp-agent/prompt-catalog.json';
import logoDarkUrl from './logo-dark.png';
import './theme.css';
import './styles.css';

interface CatalogResponse {
  environment: string;
  integrationKit: PublicIntegrationKitSummary;
  apis: PublicApiCatalogEntry[];
}

interface BuiltTryRequest {
  url: string;
  absoluteUrl: string;
  init: RequestInit;
}

interface PortalRoute {
  view: PortalView;
  documentationSlug: string;
  apiId?: string;
  operationId?: string;
}

interface TryResponseState {
  summary: string;
  body: string;
  headers: string;
  loading: boolean;
  status?: number;
  statusText?: string;
  ok?: boolean;
  duration?: number;
}

type ActiveTab = 'try' | 'reference';
type PortalView = 'overview' | 'apis' | 'mcp-tools' | 'jobs' | 'activity' | 'docs' | 'chat';
type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string; strokeWidth?: number | string }>;
type ChatPhase = 'connecting' | 'working' | 'tool' | 'answering' | 'complete' | 'stopped' | 'error';

interface CatalogPromptExecution {
  promptId: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  replyToId?: string;
  streaming?: boolean;
  phase?: ChatPhase;
  activityTools?: AgentChatToolActivity[];
  startedAt?: number;
  durationMs?: number;
  error?: string;
  retryMessage?: string;
  retryTurnId?: string;
  model?: string;
  preamble?: string;
  preambleStream?: string;
  thinking?: string;
  reasoning?: string;
  reasoningStream?: string;
  observedMcpItems?: string[];
  toolRuns?: AgentToolRun[];
  resultCards?: AgentResultCard[];
  citations?: AgentCitation[];
  profileId?: string;
  profileLabel?: string;
  retryPromptExecution?: CatalogPromptExecution;
}

const ICONS: Record<string, IconComponent> = {
  'arrow-down': ArrowDown,
  'arrow-up': ArrowUp,
  bot: Bot,
  'book-open': BookOpen,
  building: Building,
  'check-circle-2': CheckCircle2,
  clipboard: Clipboard,
  copy: Copy,
  'corner-up-left': CornerUpLeft,
  download: Download,
  'file-text': FileText,
  gauge: Gauge,
  'git-branch': GitBranch,
  globe: Globe,
  'calendar-clock': CalendarClock,
  'life-buoy': LifeBuoy,
  'list-filter': ListFilter,
  'loader-2': Loader2,
  maximize: Maximize2,
  minimize: Minimize2,
  package: Package,
  plug: Plug,
  power: Power,
  'refresh-cw': RefreshCw,
  rocket: Rocket,
  search: Search,
  send: Send,
  'shield-check': ShieldCheck,
  sparkles: Sparkles,
  square: Square,
  users: Users,
  x: X
};

const initialSession: SessionStatus = { connected: false, environment: '' };
const initialPublicConfig: PortalPublicConfig = { environment: '', customers: [], chatProfiles: [], defaultChatProfileId: '' };
const initialResponse: TryResponseState = {
  summary: 'Send a request to see the response.',
  body: 'No response yet.',
  headers: '',
  loading: false
};

const markdownHtmlCache = new WeakMap<DocumentationPage, string>();
const ENDPOINT_ROW_HEIGHT = 82;
const ENDPOINT_OVERSCAN = 8;
const CHAT_MESSAGE_MAX_LENGTH = PortalChatMessageMaxCharacters;
const CHAT_REASONING_SUMMARY_MAX_LENGTH = 12000;
const CHAT_BUSY_RETRY_DELAYS_MS = [250, 500, 1000, 2000, 5000];
const CHAT_RESUME_RETRY_DELAYS_MS = [500, 1500];
const CHAT_TOOL_DEBUG_HEADER = 'X-IAsset-Debug';
const CHAT_TOOL_DEBUG_VALUE = 'tool-details';
const CHAT_ID_STORAGE_KEY = 'iasset.portal.chatId';
const CHAT_ID_CHANNEL_NAME = 'iasset.portal.chat-tabs.v1';
const ROUTE_SCROLL_STORAGE_PREFIX = 'iasset.portal.route-scroll.v1:';

interface AiPrompt {
  label: string;
  message: string;
  icon: string;
  promptId?: string;
  primaryTools?: string[];
}

interface CategorizedAiPrompt extends AiPrompt {
  category: string;
}

const MCP_PROMPT_LIBRARY: CategorizedAiPrompt[] = mcpPromptCatalog.prompts
  .map((prompt) => ({
    label: prompt.title,
    message: prompt.prompt,
    icon: prompt.icon,
    category: prompt.category,
    promptId: prompt.id,
    primaryTools: prompt.primaryTools
  }));

const FEATURED_MCP_PROMPTS: AiPrompt[] = mcpPromptCatalog.prompts
  .filter((prompt) => prompt.featured)
  .map((prompt) => ({
    label: prompt.title,
    message: prompt.prompt,
    icon: prompt.icon,
    promptId: prompt.id,
    primaryTools: prompt.primaryTools
  }));

const AI_PROMPTS = {
  overview: FEATURED_MCP_PROMPTS.slice(0, 3),
  apis: [
    {
      label: 'Choose an endpoint',
      message: 'Which iasset.com API endpoint should I use for agreements, assets, users, or directory data?',
      icon: 'search'
    },
    {
      label: 'Explain an error',
      message: 'Explain the likely cause of a 401, 403, 429, or 5xx response in this portal and what I should check next.',
      icon: 'life-buoy'
    },
    {
      label: 'Generate request input',
      message: 'Help me build a valid request body and parameters for an iasset.com API call.',
      icon: 'send'
    }
  ],
  docs: [
    {
      label: 'Explain this guide',
      message: 'Summarize the current documentation guidance and turn it into an action checklist for my customer integration.',
      icon: 'book-open'
    },
    {
      label: 'Troubleshoot setup',
      message: 'Help me troubleshoot portal setup, authentication, and first request issues for this customer.',
      icon: 'life-buoy'
    }
  ],
  chat: FEATURED_MCP_PROMPTS
} satisfies Record<string, AiPrompt[]>;

function App(): ReactNode {
  const [route, setRoute] = useState<PortalRoute>(() => parseRoute());
  const currentRouteRef = useRef(route);
  const [mountedViews, setMountedViews] = useState<Set<PortalView>>(() => new Set([parseRoute().view]));
  const [catalogEntries, setCatalogEntries] = useState<PublicApiCatalogEntry[]>([]);
  const [integrationKit, setIntegrationKit] = useState<PublicIntegrationKitSummary>();
  const [operations, setOperations] = useState<ApiOperation[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [environmentLabel, setEnvironmentLabel] = useState('Integration workspace');
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>(initialSession);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [publicConfig, setPublicConfig] = useState<PortalPublicConfig>(initialPublicConfig);
  const [selectedApiId, setSelectedApiId] = useState('all');
  const [selectedOperationKey, setSelectedOperationKey] = useState('');
  const [activeMethod, setActiveMethod] = useState('all');
  const [operationQuery, setOperationQuery] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTab>('try');
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
  const [activeRequestBodyContentType, setActiveRequestBodyContentType] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [tryError, setTryError] = useState('');
  const [responseState, setResponseState] = useState<TryResponseState>(initialResponse);
  const [lastResponseText, setLastResponseText] = useState('');
  const [requestRunning, setRequestRunning] = useState(false);
  const [requestUrlCopied, setRequestUrlCopied] = useState(false);
  const [responseCopied, setResponseCopied] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [connectSubmitting, setConnectSubmitting] = useState(false);
  const [docsQuery, setDocsQuery] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatError, setChatError] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatId = usePortalChatId();
  const [chatConversationToken, setChatConversationToken] = useState('');
  const [chatDirectContextToken, setChatDirectContextToken] = useState('');
  const [selectedChatProfileId, setSelectedChatProfileId] = useState('');
  const chatAbortController = useRef<AbortController | null>(null);
  const chatActiveTurnId = useRef<string | null>(null);
  const chatStopRequestedTurnId = useRef<string | null>(null);
  const pendingSendAfterConnect = useRef(false);
  const pendingChatAfterConnect = useRef<{
    message: string;
    promptExecution?: CatalogPromptExecution;
  } | undefined>(undefined);

  const loadSessionStatus = useCallback(async () => {
    try {
      const response = await fetch('/portal/session/status', { cache: 'no-store' });
      if (!response.ok) {
        setSessionStatus(initialSession);
        return;
      }

      setSessionStatus((await response.json()) as SessionStatus);
    } catch {
      setSessionStatus(initialSession);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  const loadPublicConfig = useCallback(async () => {
    try {
      const response = await fetch('/portal/config.json', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Configuration returned ${response.status}.`);
      }

      const config = (await response.json()) as PortalPublicConfig;
      setPublicConfig(config);
      if (config.environment) {
        setEnvironmentLabel(`${config.environment.toUpperCase()} integration workspace`);
      }
    } catch {
      setPublicConfig(initialPublicConfig);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError('');

    try {
      const response = await fetch('/portal/catalog.json', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Catalog returned ${response.status}.`);
      }

      const catalog = (await response.json()) as CatalogResponse;
      const loadedOperations = await loadApiOperations(catalog.apis);
      setCatalogEntries(catalog.apis);
      setIntegrationKit(catalog.integrationKit);
      setOperations(loadedOperations);
      setEnvironmentLabel(`${catalog.environment.toUpperCase()} integration workspace`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load the API catalog.';
      setCatalogError(message);
      setCatalogEntries([]);
      setIntegrationKit(undefined);
      setOperations([]);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    const updateRoute = () => {
      const nextRoute = parseRoute();
      saveRouteScrollPosition(currentRouteRef.current);
      currentRouteRef.current = nextRoute;
      startTransition(() => {
        setRoute(nextRoute);
        setMountedViews((current) => {
          if (current.has(nextRoute.view)) {
            return current;
          }

          const next = new Set(current);
          next.add(nextRoute.view);
          return next;
        });
      });
    };
    window.addEventListener('hashchange', updateRoute);
    return () => window.removeEventListener('hashchange', updateRoute);
  }, []);

  useLayoutEffect(() => {
    if (sessionLoading || !sessionStatus.connected) return;
    const scrollingElement = document.scrollingElement;
    if (!scrollingElement) return;
    const top = readRouteScrollPosition(route);
    scrollingElement.scrollTo({ top, left: 0, behavior: 'auto' });
  }, [route.documentationSlug, route.view, sessionLoading, sessionStatus.connected]);

  useEffect(() => {
    void loadPublicConfig();
    void loadSessionStatus();
  }, [loadPublicConfig, loadSessionStatus]);

  useEffect(() => {
    const defaultProfileId = publicConfig.defaultChatProfileId || publicConfig.chatProfiles[0]?.id || '';
    setSelectedChatProfileId((current) => (
      current && publicConfig.chatProfiles.some((profile) => profile.id === current)
        ? current
        : defaultProfileId
    ));
  }, [publicConfig.chatProfiles, publicConfig.defaultChatProfileId]);

  useEffect(() => {
    if (sessionLoading) {
      return;
    }

    if (!sessionStatus.connected) {
      setCatalogEntries([]);
      setIntegrationKit(undefined);
      setOperations([]);
      setCatalogError('');
      setCatalogLoading(false);
      if (sessionStatus.environment) {
        setEnvironmentLabel(`${sessionStatus.environment.toUpperCase()} integration workspace`);
      }
      return;
    }

    void loadCatalog();
  }, [loadCatalog, sessionLoading, sessionStatus.connected, sessionStatus.environment]);

  const deferredOperationQuery = useDeferredValue(operationQuery);
  const normalizedQuery = deferredOperationQuery.trim().toLowerCase();
  const { filteredOperations, methodCounts } = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    for (const method of ['all', ...HTTP_METHODS]) {
      counts[method] = 0;
    }

    const filtered: ApiOperation[] = [];
    for (const operation of operations) {
      const matchesApi = selectedApiId === 'all' || operation.apiId === selectedApiId;
      const matchesQuery = !normalizedQuery || operation.searchText.includes(normalizedQuery);
      if (!matchesApi || !matchesQuery) {
        continue;
      }

      counts.all += 1;
      counts[operation.method] = (counts[operation.method] ?? 0) + 1;
      if (activeMethod === 'all' || operation.method === activeMethod) {
        filtered.push(operation);
      }
    }

    return { filteredOperations: filtered, methodCounts: counts };
  }, [activeMethod, normalizedQuery, operations, selectedApiId]);

  const operationByKey = useMemo(
    () => new Map(operations.map((operation) => [operation.key, operation])),
    [operations]
  );

  useEffect(() => {
    if (route.view !== 'apis' || operations.length === 0) {
      return;
    }

    const requestedApiId = route.apiId?.trim();
    if (!requestedApiId || !operations.some((operation) => operation.apiId === requestedApiId)) {
      return;
    }

    setSelectedApiId(requestedApiId);
    setActiveMethod('all');
    setOperationQuery('');
  }, [operations, route]);

  useEffect(() => {
    if (catalogLoading) {
      return;
    }

    if (filteredOperations.length === 0) {
      if (selectedOperationKey) {
        setSelectedOperationKey('');
      }
      return;
    }

    const requestedOperationId = route.view === 'apis' ? route.operationId?.trim() : '';
    const requestedOperation = requestedOperationId
      ? filteredOperations.find((operation) => operation.operationId === requestedOperationId)
      : undefined;
    if (requestedOperation && requestedOperation.key !== selectedOperationKey) {
      setSelectedOperationKey(requestedOperation.key);
      return;
    }

    if (!filteredOperations.some((operation) => operation.key === selectedOperationKey)) {
      setSelectedOperationKey(filteredOperations[0].key);
    }
  }, [catalogLoading, filteredOperations, route, selectedOperationKey]);

  const selectedOperation = operationByKey.get(selectedOperationKey);

  useEffect(() => {
    if (!selectedOperation) {
      setParameterValues({});
      setRequestBody('');
      setActiveRequestBodyContentType('');
      setResponseState(initialResponse);
      setLastResponseText('');
      return;
    }

    const bodyOption = getPreferredRequestBody(selectedOperation);
    const methodAllowsBody = selectedOperation.method !== 'GET' && selectedOperation.method !== 'HEAD';
    setParameterValues(createInitialParameterValues(selectedOperation));
    setTryError('');
    setResponseState(initialResponse);
    setLastResponseText('');
    setActiveTab('try');

    if (bodyOption && methodAllowsBody) {
      setActiveRequestBodyContentType(bodyOption.contentType);
      setRequestBody(bodyOption.sampleText);
      return;
    }

    setActiveRequestBodyContentType('');
    setRequestBody('');
  }, [selectedOperation?.key]);

  const connect = useCallback(async (formData: FormData) => {
    setConnectError('');
    setConnectSubmitting(true);

    try {
      const response = await fetch('/portal/session/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          customerCode: String(formData.get('customerCode') ?? ''),
          username: String(formData.get('username') ?? ''),
          password: String(formData.get('password') ?? '')
        })
      });

      const status = (await response.json()) as SessionStatus & { message?: string; error?: string; detail?: string };
      if (!response.ok || !status.connected) {
        setConnectError(status.message ?? status.error ?? status.detail ?? 'Connection failed.');
        return false;
      }

      setSessionStatus(status);
      setConnectOpen(false);
      return true;
    } finally {
      setConnectSubmitting(false);
    }
  }, []);

  const cancelActiveChat = useCallback(async () => {
    const turnId = chatActiveTurnId.current;
    chatActiveTurnId.current = null;
    if (turnId) {
      chatStopRequestedTurnId.current = turnId;
    }
    const cancellation = turnId
      ? fetch('/portal/session/chat/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ turnId })
      }).catch(() => undefined)
      : Promise.resolve(undefined);
    await cancellation;
    chatAbortController.current?.abort();
  }, []);

  const disconnect = useCallback(async () => {
    await cancelActiveChat();
    const response = await fetch('/portal/session/disconnect', {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      }
    });

    if (response.ok) {
      setSessionStatus((await response.json()) as SessionStatus);
      setChatMessages([]);
      setChatInput('');
      setChatError('');
      setChatConversationToken('');
      setChatDirectContextToken('');
    }
  }, [cancelActiveChat]);

  const openConnectDialog = useCallback((message = '') => {
    setConnectError(message);
    setConnectOpen(true);
  }, []);

  const sendTryRequest = useCallback(async () => {
    if (!selectedOperation) {
      return;
    }

    if (!sessionStatus.connected) {
      pendingSendAfterConnect.current = true;
      openConnectDialog('Connect to send this request.');
      return;
    }

    setTryError('');
    setRequestRunning(true);
    setResponseState({
      summary: `Sending ${selectedOperation.method} ${selectedOperation.path}`,
      body: 'Waiting for response...',
      headers: '',
      loading: true
    });
    setLastResponseText('');

    try {
      const builtRequest = buildTryRequest(selectedOperation, parameterValues, activeRequestBodyContentType, requestBody);
      const startedAt = performance.now();
      const response = await fetch(builtRequest.url, builtRequest.init);
      const duration = Math.max(1, Math.round(performance.now() - startedAt));
      const text = await response.text();
      setLastResponseText(text);
      setResponseState({
        summary: '',
        body: formatResponseBody(text, response.headers.get('content-type')),
        headers: Array.from(response.headers.entries()).map(([name, value]) => `${name}: ${value}`).join('\n') || 'No response headers.',
        loading: false,
        status: response.status,
        statusText: response.statusText || 'Response',
        ok: response.ok,
        duration
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Request failed.';
      setTryError(message);
      setResponseState({
        summary: 'Request failed before a response was received.',
        body: message,
        headers: '',
        loading: false
      });
    } finally {
      setRequestRunning(false);
    }
  }, [activeRequestBodyContentType, openConnectDialog, parameterValues, requestBody, selectedOperation, sessionStatus.connected]);

  const copyCurrentRequestUrl = useCallback(async () => {
    if (!selectedOperation) {
      return;
    }

    try {
      const builtRequest = buildTryRequest(selectedOperation, parameterValues, activeRequestBodyContentType, requestBody, false);
      await navigator.clipboard.writeText(builtRequest.absoluteUrl);
      setRequestUrlCopied(true);
      window.setTimeout(() => setRequestUrlCopied(false), 1200);
    } catch (error) {
      setTryError(error instanceof Error ? error.message : 'Unable to copy request URL.');
    }
  }, [activeRequestBodyContentType, parameterValues, requestBody, selectedOperation]);

  const copyResponse = useCallback(async () => {
    if (!lastResponseText) {
      return;
    }

    await navigator.clipboard.writeText(lastResponseText);
    setResponseCopied(true);
    window.setTimeout(() => setResponseCopied(false), 1000);
  }, [lastResponseText]);

  const sendChatMessage = useCallback(async (
    messageOverride?: string,
    promptExecution?: CatalogPromptExecution,
    retryTurnId?: string
  ) => {
    const message = (messageOverride ?? chatInput).trim();
    if (!message || chatSending) {
      return;
    }

    if (!sessionStatus.connected) {
      pendingChatAfterConnect.current = { message, promptExecution };
      openConnectDialog('Connect to use chat.');
      return;
    }

    setChatInput('');
    setChatError('');
    setChatSending(true);
    const startedAt = Date.now();
    const turnId = retryTurnId ?? window.crypto.randomUUID();
    chatActiveTurnId.current = turnId;
    chatStopRequestedTurnId.current = null;
    const messageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;
    const abortController = new AbortController();
    chatAbortController.current?.abort();
    chatAbortController.current = abortController;
    setChatMessages((current) => ([
      ...current,
      {
        id: messageId,
        role: 'user',
        text: message
      },
      {
        id: assistantMessageId,
        role: 'assistant',
        text: '',
        replyToId: messageId,
        streaming: true,
        phase: 'connecting',
        startedAt,
        retryMessage: message,
        retryTurnId: turnId,
        retryPromptExecution: promptExecution,
        activityTools: []
      }
    ]));

    let streamedText = '';
    let streamedPreamble = '';
    let streamedReasoning = '';
    let answerStarted = false;
    let startingAfter: number | undefined;
    let animationFrame: number | undefined;
    const updateAssistantMessage = (update: Partial<ChatMessage>) => {
      setChatMessages((current) => current.map((chatMessage) => (
        chatMessage.id === assistantMessageId
          ? { ...chatMessage, ...update }
          : chatMessage
      )));
    };
    const isTurnStopped = () => abortController.signal.aborted
      || chatStopRequestedTurnId.current === turnId;
    const flushStreamedText = () => {
      animationFrame = undefined;
      if (isTurnStopped()) {
        return;
      }

      const nextText = streamedText;
      updateAssistantMessage({ text: nextText });
    };
    const onTextDelta = (delta: string) => {
      if (isTurnStopped()) {
        return;
      }

      if (!answerStarted) {
        answerStarted = true;
        updateAssistantMessage({ phase: 'answering' });
      }
      streamedText += delta;
      if (animationFrame === undefined) {
        animationFrame = window.requestAnimationFrame(flushStreamedText);
      }
    };
    const onReasoningDelta = (delta: string) => {
      if (isTurnStopped() || streamedReasoning.length >= CHAT_REASONING_SUMMARY_MAX_LENGTH) {
        return;
      }

      streamedReasoning = `${streamedReasoning}${delta}`.slice(0, CHAT_REASONING_SUMMARY_MAX_LENGTH);
      updateAssistantMessage({
        reasoningStream: streamedReasoning,
        phase: answerStarted ? 'answering' : 'working'
      });
    };
    const onPreambleDelta = (delta: string) => {
      if (isTurnStopped() || streamedPreamble.length >= CHAT_REASONING_SUMMARY_MAX_LENGTH) {
        return;
      }

      streamedPreamble = `${streamedPreamble}${delta}`.slice(0, CHAT_REASONING_SUMMARY_MAX_LENGTH);
      updateAssistantMessage({
        preambleStream: streamedPreamble,
        phase: answerStarted ? 'answering' : 'working'
      });
    };
    const onToolActivity = (tool: AgentChatToolActivity) => {
      if (isTurnStopped()) {
        return;
      }

      setChatMessages((current) => current.map((chatMessage) => {
        if (chatMessage.id !== assistantMessageId) {
          return chatMessage;
        }

        const tools = [...(chatMessage.activityTools ?? [])];
        const existingIndex = tools.findIndex((item) => item.id === tool.id);
        if (existingIndex >= 0) {
          tools[existingIndex] = tool;
        } else {
          tools.push(tool);
        }

        return {
          ...chatMessage,
          phase: answerStarted ? 'answering' : 'tool',
          activityTools: tools
        };
      }));
    };

    const requestInit: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          [CHAT_TOOL_DEBUG_HEADER]: CHAT_TOOL_DEBUG_VALUE
        },
        credentials: 'same-origin',
        signal: abortController.signal
    };

    try {
      let chatResponse: AgentChatResponse;
      for (let resumeAttempt = 0; ; resumeAttempt += 1) {
        requestInit.body = JSON.stringify({
          chatId,
          turnId,
          message,
          conversationToken: chatConversationToken || undefined,
          directContextToken: chatDirectContextToken || undefined,
          profileId: selectedChatProfileId || undefined,
          promptId: promptExecution?.promptId,
          ...(startingAfter === undefined ? {} : { startingAfter })
        });
        let response: Response;
        try {
          response = await fetchPortalChatWithBusyRetry(requestInit);
        } catch (error) {
          if (isTurnStopped()) {
            throw error;
          }
          const delay = CHAT_RESUME_RETRY_DELAYS_MS[resumeAttempt];
          if (delay === undefined || !isRetryablePortalChatTransportFailure(error)) {
            throw error;
          }
          await waitForChatRetry(delay, abortController.signal);
          continue;
        }

        if (response.status === 401) {
          pendingChatAfterConnect.current = { message, promptExecution };
          setConnectError('Sign in to continue chat.');
          setSessionStatus({ connected: false, environment: sessionStatus.environment });
          setChatConversationToken('');
          setChatDirectContextToken('');
          setChatMessages((current) => current.filter((chatMessage) => (
            chatMessage.id !== messageId && chatMessage.id !== assistantMessageId
          )));
          return;
        }

        if (!response.ok) {
          const bodyText = await response.text();
          throw new Error(getChatHttpErrorMessage(response.status, bodyText));
        }

        try {
          chatResponse = await readPortalChatResponse(response, {
            onReady: () => {
              if (!isTurnStopped()) {
                updateAssistantMessage({ phase: 'working' });
              }
            },
            onStarted: () => {
              if (!isTurnStopped()) {
                updateAssistantMessage({ phase: 'working' });
              }
            },
            onTool: onToolActivity,
            onSequenceNumber: (sequenceNumber) => {
              startingAfter = Math.max(startingAfter ?? -1, sequenceNumber);
            },
            onPreambleDelta,
            onReasoningDelta,
            onTextDelta
          });
          break;
        } catch (error) {
          if (isTurnStopped()) {
            throw error;
          }
          const delay = CHAT_RESUME_RETRY_DELAYS_MS[resumeAttempt];
          if (delay === undefined || !isRetryablePortalChatTransportFailure(error)) {
            throw error;
          }
          await waitForChatRetry(delay, abortController.signal);
        }
      }
      if (isTurnStopped()) {
        updateAssistantMessage({
          text: streamedText,
          streaming: false,
          phase: 'stopped',
          durationMs: Date.now() - startedAt
        });
        return;
      }
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
      flushStreamedText();
      setChatConversationToken(chatResponse.conversationToken ?? '');
      setChatDirectContextToken(chatResponse.directContextToken ?? '');
      setChatMessages((current) => current.map((chatMessage) => (
        chatMessage.id === assistantMessageId
          ? {
            ...chatMessage,
            streaming: false,
            phase: 'complete',
            durationMs: Date.now() - startedAt,
            text: chatResponse.reply || streamedText,
            model: chatResponse.model,
            preamble: chatResponse.preamble || streamedPreamble,
            preambleStream: undefined,
            thinking: chatResponse.thinking ?? chatResponse.reasoning,
            reasoning: chatResponse.reasoning,
            reasoningStream: undefined,
            observedMcpItems: chatResponse.observedMcpItems,
            toolRuns: chatResponse.toolRuns,
            resultCards: chatResponse.resultCards,
            citations: chatResponse.citations,
            profileId: chatResponse.profileId,
            profileLabel: chatResponse.profileLabel
          }
          : chatMessage
      )));
    } catch (error) {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
      const stopped = isTurnStopped();
      const errorMessage = stopped ? undefined : getChatErrorMessage(error);
      updateAssistantMessage({
        text: streamedText,
        streaming: false,
        phase: stopped ? 'stopped' : 'error',
        durationMs: Date.now() - startedAt,
        error: errorMessage,
        ...(errorMessage?.includes('Running it again may repeat MCP actions.')
          ? { retryTurnId: undefined }
          : {})
      });
      if (stopped) {
        return;
      }
      setChatError(errorMessage ?? 'Chat request failed.');
    } finally {
      if (chatAbortController.current === abortController) {
        chatAbortController.current = null;
      }
      if (chatActiveTurnId.current === turnId) {
        chatActiveTurnId.current = null;
      }
      if (chatStopRequestedTurnId.current === turnId) {
        chatStopRequestedTurnId.current = null;
      }
      setChatSending(false);
    }
  }, [chatConversationToken, chatDirectContextToken, chatId, chatInput, chatSending, openConnectDialog, selectedChatProfileId, sessionStatus.connected, sessionStatus.environment]);

  const resetChatConversation = useCallback(() => {
    void cancelActiveChat();
    setChatMessages([]);
    setChatConversationToken('');
    setChatDirectContextToken('');
    setChatError('');
  }, [cancelActiveChat]);

  const changeChatProfile = useCallback((profileId: string) => {
    setSelectedChatProfileId((current) => {
      if (current === profileId) {
        return current;
      }

      void cancelActiveChat();
      setChatMessages([]);
      setChatConversationToken('');
      setChatDirectContextToken('');
      setChatError('');
      return profileId;
    });
  }, [cancelActiveChat]);

  const sendChatPrompt = useCallback((message: string, prompt?: AiPrompt) => {
    if (window.location.hash !== '#chat') {
      window.location.hash = 'chat';
    }

    if (chatSending) {
      setChatInput(message);
      return;
    }

    const promptExecution = prompt?.promptId
      ? { promptId: prompt.promptId }
      : undefined;
    void sendChatMessage(message, promptExecution);
  }, [chatSending, sendChatMessage]);

  const handleConnectSubmit = useCallback(async (formData: FormData) => {
    const connected = await connect(formData);
    if (connected && pendingSendAfterConnect.current) {
      pendingSendAfterConnect.current = false;
      window.setTimeout(() => void sendTryRequest(), 0);
    }

    if (connected && pendingChatAfterConnect.current) {
      const pending = pendingChatAfterConnect.current;
      pendingChatAfterConnect.current = undefined;
      window.setTimeout(() => void sendChatMessage(pending.message, pending.promptExecution), 0);
    }

    return connected;
  }, [connect, sendChatMessage, sendTryRequest]);

  const selectOperation = useCallback((operationKey: string) => {
    startTransition(() => {
      setSelectedOperationKey((current) => current === operationKey ? current : operationKey);
      setActiveTab((current) => current === 'try' ? current : 'try');
    });
  }, []);

  const selectApi = useCallback((apiId: string) => {
    startTransition(() => setSelectedApiId((current) => current === apiId ? current : apiId));
  }, []);

  const selectMethod = useCallback((method: string) => {
    startTransition(() => setActiveMethod((current) => current === method ? current : method));
  }, []);

  const updateOperationQuery = useCallback((query: string) => {
    startTransition(() => setOperationQuery(query));
  }, []);

  const updateDocsQuery = useCallback((query: string) => {
    startTransition(() => setDocsQuery(query));
  }, []);

  const updateParameterValue = useCallback((key: string, value: string) => {
    setParameterValues((current) => current[key] === value ? current : { ...current, [key]: value });
  }, []);

  const superUser = sessionStatus.isSuperUser === true;
  const fullProductAccess = superUser || sessionStatus.permissions?.some((product) => product === 'customer-api' || product === 'mcp-api') === true;
  const mcpAccess = superUser || sessionStatus.permissions?.includes('mcp-api') === true;
  useEffect(() => {
    if (sessionStatus.connected && ((!fullProductAccess && route.view === 'overview')
      || (!mcpAccess && route.view === 'mcp-tools')
      || (!superUser && ['jobs', 'chat'].includes(route.view)))) {
      window.location.hash = '#apis';
    }
  }, [sessionStatus.connected, fullProductAccess, mcpAccess, superUser, route.view]);

  const loginEnvironmentLabel = sessionStatus.environment
    ? `${sessionStatus.environment.toUpperCase()} integration workspace`
    : environmentLabel;

  if (sessionLoading) {
    return <PortalLoadingScreen environmentLabel={loginEnvironmentLabel} />;
  }

  if (!sessionStatus.connected) {
    return (
      <LoginScreen
        environmentLabel={loginEnvironmentLabel}
        error={connectError}
        submitting={connectSubmitting}
        onSubmit={(formData) => void handleConnectSubmit(formData)}
      />
    );
  }

  return (
    <div className={classNames('app-shell', route.view === 'chat' && 'chat-route')}>
      <Topbar
        environmentLabel={environmentLabel}
        route={route}
        sessionStatus={sessionStatus}
        onConnect={() => openConnectDialog()}
        onDisconnect={() => void disconnect()}
      />

      <main className="portal-workspace">
        {fullProductAccess && mountedViews.has('overview') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'overview' && 'inactive')}
            aria-hidden={route.view !== 'overview'}
            inert={route.view !== 'overview' ? true : undefined}
          >
            <OverviewView
              apiCount={catalogEntries.length}
              operationCount={operations.length}
              canAsk={superUser}
              canUseMcp={mcpAccess}
              onAskAi={sendChatPrompt}
            />
          </div>
        )}

        {mountedViews.has('apis') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'apis' && 'inactive')}
            aria-hidden={route.view !== 'apis'}
            inert={route.view !== 'apis' ? true : undefined}
          >
            <ApisView
              activeMethod={activeMethod}
              activeRequestBodyContentType={activeRequestBodyContentType}
              activeTab={activeTab}
              catalogEntries={catalogEntries}
              catalogError={catalogError}
              catalogLoading={catalogLoading}
              integrationKit={integrationKit}
              copyCurrentRequestUrl={() => void copyCurrentRequestUrl()}
              copyResponse={() => void copyResponse()}
              filteredOperations={filteredOperations}
              lastResponseText={lastResponseText}
              methodCounts={methodCounts}
              operationQuery={operationQuery}
              operations={operations}
              parameterValues={parameterValues}
              requestBody={requestBody}
              requestRunning={requestRunning}
              requestUrlCopied={requestUrlCopied}
              responseCopied={responseCopied}
              responseState={responseState}
              selectedApiId={selectedApiId}
              selectedOperation={selectedOperation}
              selectedOperationKey={selectedOperationKey}
              sessionStatus={sessionStatus}
              tryError={tryError}
              onActiveMethodChange={selectMethod}
              onActiveTabChange={setActiveTab}
              onConnect={() => openConnectDialog()}
              onOperationQueryChange={updateOperationQuery}
              onParameterValueChange={updateParameterValue}
              onRefreshCatalog={() => void loadCatalog()}
              onRequestBodyChange={setRequestBody}
              onSelectedApiChange={selectApi}
              onSelectedOperationChange={selectOperation}
              onAskAi={sendChatPrompt}
              onSendTryRequest={() => void sendTryRequest()}
            />
          </div>
        )}

        {mcpAccess && mountedViews.has('mcp-tools') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'mcp-tools' && 'inactive')}
            aria-hidden={route.view !== 'mcp-tools'}
            inert={route.view !== 'mcp-tools' ? true : undefined}
          >
            <McpToolsView
              sessionStatus={sessionStatus}
              onConnect={() => openConnectDialog('Connect to run metadata tools with your authorized MCP scope.')}
            />
          </div>
        )}

        {superUser && mountedViews.has('jobs') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'jobs' && 'inactive')}
            aria-hidden={route.view !== 'jobs'}
            inert={route.view !== 'jobs' ? true : undefined}
          >
            <JobsView active={route.view === 'jobs'} sessionStatus={sessionStatus} />
          </div>
        )}

        {mountedViews.has('activity') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'activity' && 'inactive')}
            aria-hidden={route.view !== 'activity'}
            inert={route.view !== 'activity' ? true : undefined}
          >
            <ActivityView
              active={route.view === 'activity'}
              sessionStatus={sessionStatus}
              onAskAi={sendChatPrompt}
            />
          </div>
        )}

        {mountedViews.has('docs') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'docs' && 'inactive')}
            aria-hidden={route.view !== 'docs'}
            inert={route.view !== 'docs' ? true : undefined}
          >
            <DocsView
              catalogCount={catalogEntries.length}
              canAsk={superUser}
              docsQuery={docsQuery}
              selectedSlug={route.documentationSlug}
              onAskAi={sendChatPrompt}
              onDocsQueryChange={updateDocsQuery}
            />
          </div>
        )}

        {superUser && mountedViews.has('chat') && (
          <div
            className={classNames('portal-route-panel', route.view !== 'chat' && 'inactive')}
            aria-hidden={route.view !== 'chat'}
            inert={route.view !== 'chat' ? true : undefined}
          >
            <ChatView
              chatError={chatError}
              chatInput={chatInput}
              chatMessages={chatMessages}
              chatSending={chatSending}
              chatProfiles={publicConfig.chatProfiles}
              selectedChatProfileId={selectedChatProfileId}
              sessionStatus={sessionStatus}
              onChatInputChange={setChatInput}
              onChatProfileChange={changeChatProfile}
              onConnect={() => openConnectDialog()}
              onResetConversation={resetChatConversation}
              onRetryChatMessage={(message, promptExecution, turnId) => void sendChatMessage(message, promptExecution, turnId)}
              onSendChatPrompt={sendChatPrompt}
              onSendChatMessage={() => void sendChatMessage()}
              onStopChat={() => void cancelActiveChat()}
            />
          </div>
        )}
      </main>

      <ConnectDialog
        error={connectError}
        open={connectOpen}
        submitting={connectSubmitting}
        onClose={() => setConnectOpen(false)}
        onSubmit={(formData) => void handleConnectSubmit(formData)}
      />
    </div>
  );
}

function usePortalChatId(): string {
  const [chatId, setChatId] = useState(getOrCreatePortalChatId);
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel(CHAT_ID_CHANNEL_NAME);
    const instanceId = window.crypto.randomUUID();
    const startedAt = Date.now();
    const claim = () => channel.postMessage({
      type: 'claim',
      chatId: chatIdRef.current,
      instanceId,
      startedAt
    });
    const rotate = () => {
      chatIdRef.current = window.crypto.randomUUID();
      window.sessionStorage.setItem(CHAT_ID_STORAGE_KEY, chatIdRef.current);
      setChatId(chatIdRef.current);
      claim();
    };

    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (!event.data || typeof event.data !== 'object' || Array.isArray(event.data)) {
        return;
      }
      const message = event.data as Record<string, unknown>;
      if (
        message.type !== 'claim'
        || message.chatId !== chatIdRef.current
        || message.instanceId === instanceId
        || typeof message.instanceId !== 'string'
        || typeof message.startedAt !== 'number'
      ) {
        return;
      }

      const thisPageIsNewer = startedAt > message.startedAt
        || (startedAt === message.startedAt && instanceId > message.instanceId);
      if (thisPageIsNewer) {
        rotate();
      } else {
        claim();
      }
    };
    claim();
    return () => channel.close();
  }, []);

  return chatId;
}

function getOrCreatePortalChatId(): string {
  const storedChatId = window.sessionStorage.getItem(CHAT_ID_STORAGE_KEY);
  if (storedChatId && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(storedChatId)) {
    return storedChatId;
  }

  const chatId = window.crypto.randomUUID();
  window.sessionStorage.setItem(CHAT_ID_STORAGE_KEY, chatId);
  return chatId;
}

function PortalLoadingScreen({ environmentLabel }: { environmentLabel: string }): ReactNode {
  return (
    <main className="login-screen loading-screen" aria-label="iasset One loading">
      <section className="login-brand-panel">
        <div className="login-brand-lockup">
          <span className="login-brand-mark" aria-hidden="true"><img src={logoDarkUrl} alt="" /></span>
          <span>{environmentLabel}</span>
        </div>
        <h1 className="one-wordmark">iasset <span>One</span></h1>
      </section>
      <section className="login-card login-loading-card" aria-live="polite">
        <Icon name="loader-2" />
        <strong>Checking session</strong>
      </section>
    </main>
  );
}

function LoginScreen({ environmentLabel, error, submitting, onSubmit }: {
  environmentLabel: string;
  error: string;
  submitting: boolean;
  onSubmit(formData: FormData): void;
}): ReactNode {
  const formRef = useRef<HTMLFormElement>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formRef.current;
    if (form) {
      onSubmit(new FormData(form));
      const passwordInput = form.elements.namedItem('password');
      if (passwordInput instanceof HTMLInputElement) passwordInput.value = '';
    }
  };

  return (
    <main className="login-screen" aria-label="iasset One sign in">
      <section className="login-brand-panel">
        <div className="login-brand-lockup">
          <span className="login-brand-mark" aria-hidden="true"><img src={logoDarkUrl} alt="" /></span>
          <span>{environmentLabel}</span>
        </div>
        <div className="login-copy">
          <p className="eyebrow">The integration workspace for people and agents</p>
          <h1 className="one-wordmark">iasset <span>One</span></h1>
          <p>Connect once to explore data, run agent-powered workflows, build integrations, and operate across iasset.</p>
        </div>
        <div className="login-summary-grid" aria-label="iasset One capabilities">
          <span><Icon name="sparkles" /><strong>Ask and act</strong></span>
          <span><Icon name="bot" /><strong>Agent tools</strong></span>
          <span><Icon name="send" /><strong>Connected APIs</strong></span>
        </div>
      </section>

      <section className="login-card" aria-label="Sign in">
        <form ref={formRef} className="connect-form login-form" aria-busy={submitting} onSubmit={submit}>
          <div className="login-form-heading">
            <span className="login-badge"><Icon name="plug" /> Customer credentials</span>
            <h2>Sign in to continue</h2>
          </div>
          <ConnectionFields
            error={error}
            autoFocusFirst
          />
          <button className="primary-button connect-submit-button login-submit-button" type="submit" disabled={submitting} aria-live="polite">
            <Icon name={submitting ? 'loader-2' : 'shield-check'} />
            <span>{submitting ? 'Connecting' : 'Connect'}</span>
          </button>
        </form>
      </section>
    </main>
  );
}

function Topbar({ environmentLabel, route, sessionStatus, onConnect, onDisconnect }: {
  environmentLabel: string;
  route: PortalRoute;
  sessionStatus: SessionStatus;
  onConnect(): void;
  onDisconnect(): void;
}): ReactNode {
  const activeNavigationKey = getActiveNavigationKey(route);

  return (
    <header className="topbar">
      <div className="brand-block">
        <span className="brand-mark" aria-hidden="true">
          <img src={logoDarkUrl} alt="" />
        </span>
        <div>
          <h1 className="one-wordmark">iasset <span>One</span></h1>
          <p>{environmentLabel}</p>
        </div>
      </div>

      <nav className="portal-nav" aria-label="iasset One sections">
        {(sessionStatus.isSuperUser || sessionStatus.permissions?.some((product) => product === 'customer-api' || product === 'mcp-api')) && (
          <NavLink href="#overview" navKey="overview" activeKey={activeNavigationKey}>Home</NavLink>
        )}
        <NavLink href="#apis" navKey="apis" activeKey={activeNavigationKey}>APIs</NavLink>
        {(sessionStatus.isSuperUser || sessionStatus.permissions?.includes('mcp-api')) && (
          <NavLink href="#mcp-tools" navKey="mcp-tools" activeKey={activeNavigationKey}>Agent Tools</NavLink>
        )}
        {sessionStatus.isSuperUser && (
          <>
            <NavLink href="#jobs" navKey="jobs" activeKey={activeNavigationKey}>Jobs</NavLink>
            <NavLink href="#chat" navKey="chat" activeKey={activeNavigationKey}>Ask</NavLink>
          </>
        )}
        <NavLink href="#activity" navKey="activity" activeKey={activeNavigationKey}>Activity</NavLink>
        <NavLink href="#docs/home" navKey="docs" activeKey={activeNavigationKey}>Guides</NavLink>
        <NavLink href="#docs/regional-guidance-sla-rate-limits" navKey="environments" activeKey={activeNavigationKey}>Environments</NavLink>
        <NavLink href="#docs/troubleshooting" navKey="support" activeKey={activeNavigationKey}>Support</NavLink>
      </nav>

      <div className="session-actions">
        <span className={classNames('status-pill', sessionStatus.connected ? 'connected' : 'disconnected')}>
          {sessionStatus.connected ? `${sessionStatus.customerCode ?? 'Connected'} connected` : 'Disconnected'}
        </span>
        {!sessionStatus.connected && (
          <button className="primary-button" type="button" onClick={onConnect}>
            <Icon name="plug" />
            <span>Connect</span>
          </button>
        )}
        {sessionStatus.connected && (
          <button className="icon-button" type="button" title="Disconnect" aria-label="Disconnect" onClick={onDisconnect}>
            <Icon name="power" />
          </button>
        )}
      </div>
    </header>
  );
}

function NavLink({ href, navKey, activeKey, children }: {
  href: string;
  navKey: string;
  activeKey: string;
  children: ReactNode;
}): ReactNode {
  const selected = navKey === activeKey;
  return (
    <a className={selected ? 'selected' : undefined} href={href} data-nav-key={navKey} aria-current={selected ? 'page' : undefined}>
      {children}
    </a>
  );
}

function OverviewView({ apiCount, operationCount, canAsk, canUseMcp, onAskAi }: {
  apiCount: number;
  operationCount: number;
  canAsk: boolean;
  canUseMcp: boolean;
  onAskAi(message: string): void;
}): ReactNode {
  return (
    <section className="portal-view overview-view" aria-label="iasset One home">
      <div className="portal-hero">
        <div className="portal-hero-copy">
          <p className="eyebrow">People + agents + integrations</p>
          <h2>One place to connect, build, and operate.</h2>
          <p>Move from question to action in one authenticated workspace. Explore customer data, run governed agent tools, build against live APIs, and keep the evidence behind every result.</p>
          <div className="hero-actions">
            {canAsk && <button className="primary-link-button" type="button" onClick={() => onAskAi('Help me explore this customer. Start with the most useful agreements, assets, users, and API endpoints I should inspect.')}>
              <Icon name="sparkles" />
              <span>Ask iasset One</span>
            </button>}
            {canUseMcp && <a className="secondary-link-button" href="#mcp-tools">
              <Icon name="bot" />
              <span>Explore agent tools</span>
            </a>}
            <a className="secondary-link-button" href="#apis">
              <Icon name="send" />
              <span>Build with APIs</span>
            </a>
          </div>
        </div>
        {canAsk && <aside className="portal-hero-ai" aria-label="AI chat quick starts">
          <div className="ai-feature-heading">
            <span className="ai-feature-icon"><Icon name="sparkles" /></span>
            <div>
              <strong>Your connected intelligence layer</strong>
              <span>Ask natural-language questions, invoke governed tools, and inspect the sources behind every answer.</span>
            </div>
          </div>
          <PromptChips prompts={AI_PROMPTS.overview} onSelect={onAskAi} />
          <div className="ai-stat-grid" aria-label="Portal capabilities">
            <span><strong>{apiCount}</strong> APIs</span>
            <span><strong>{operationCount}</strong> endpoints</span>
            <span><strong>MCP</strong> data</span>
          </div>
        </aside>}
      </div>

      <div className="overview-card-grid">
        {canAsk && <OverviewCard href="#chat" icon="sparkles" title="Ask">
          Explore customer data and turn questions into grounded, actionable answers.
        </OverviewCard>}
        {canUseMcp && <OverviewCard href="#mcp-tools" icon="bot" title="Agent Tools">
          Discover governed capabilities, inspect their sources, and understand what agents can execute.
        </OverviewCard>}
        <OverviewCard href="#apis" icon="send" title="APIs">
          Search live contracts, build requests, and call endpoints in your authenticated context.
        </OverviewCard>
        <OverviewCard href="#docs/home" icon="book-open" title="Guides">
          Move from onboarding to production with quickstarts and integration guidance.
        </OverviewCard>
        <OverviewCard href="#docs/troubleshooting" icon="life-buoy" title="Operate">
          Troubleshoot requests, understand environments, and resolve common platform errors.
        </OverviewCard>
        <OverviewCard href="#activity" icon="gauge" title="Activity">
          Inspect recent operations, replay work, and package customer-scoped evidence for support.
        </OverviewCard>
      </div>
    </section>
  );
}

function OverviewCard({ href, icon, title, children }: {
  href: string;
  icon: string;
  title: string;
  children: ReactNode;
}): ReactNode {
  return (
    <a className="overview-card" href={href}>
      <Icon name={icon} />
      <strong>{title}</strong>
      <span>{children}</span>
    </a>
  );
}

function ApisView(props: {
  activeMethod: string;
  activeRequestBodyContentType: string;
  activeTab: ActiveTab;
  catalogEntries: PublicApiCatalogEntry[];
  catalogError: string;
  catalogLoading: boolean;
  integrationKit: PublicIntegrationKitSummary | undefined;
  copyCurrentRequestUrl(): void;
  copyResponse(): void;
  filteredOperations: ApiOperation[];
  lastResponseText: string;
  methodCounts: Record<string, number>;
  operationQuery: string;
  operations: ApiOperation[];
  parameterValues: Record<string, string>;
  requestBody: string;
  requestRunning: boolean;
  requestUrlCopied: boolean;
  responseCopied: boolean;
  responseState: TryResponseState;
  selectedApiId: string;
  selectedOperation: ApiOperation | undefined;
  selectedOperationKey: string;
  sessionStatus: SessionStatus;
  tryError: string;
  onActiveMethodChange(method: string): void;
  onActiveTabChange(tab: ActiveTab): void;
  onConnect(): void;
  onOperationQueryChange(query: string): void;
  onParameterValueChange(key: string, value: string): void;
  onRefreshCatalog(): void;
  onRequestBodyChange(value: string): void;
  onSelectedApiChange(apiId: string): void;
  onSelectedOperationChange(operationKey: string): void;
  onAskAi(message: string): void;
  onSendTryRequest(): void;
}): ReactNode {
  const apiPrompts = getApiPrompts(props.selectedOperation);
  const canAsk = props.sessionStatus.isSuperUser === true;
  const fullProductAccess = canAsk || props.sessionStatus.permissions?.some((product) => product === 'customer-api' || product === 'mcp-api') === true;
  const crmOnly = !fullProductAccess && props.sessionStatus.permissions?.some((product) => product === 'crm-api' || product === 'salesforce-api') === true;

  return (
    <section className="portal-view" aria-label="API discovery and Try-It">
      <section className="discovery-band" aria-label="API discovery">
        <div className="discovery-copy">
          <p className="eyebrow">API workbench</p>
          <h2>{canAsk ? 'Find an endpoint. Ask AI. Send a request.' : 'Find an endpoint. Send a request.'}</h2>
          <p>{canAsk ? 'Search published APIs, inspect contracts, ask the MCP assistant for customer context, and run requests through the secure portal proxy.' : 'Search assigned APIs, inspect contracts, and run requests through the secure portal proxy.'}</p>
        </div>
        <div className="discovery-tools">
          <label className="search-box" htmlFor="operation-search">
            <Icon name="search" />
            <input
              id="operation-search"
              type="search"
              placeholder="Search APIs, endpoints, tags, methods"
              autoComplete="off"
              value={props.operationQuery}
              onChange={(event) => props.onOperationQueryChange(event.currentTarget.value)}
            />
          </label>
          <div className="summary-strip" aria-label="Catalog summary">
            <span><strong>{crmOnly ? Math.min(1, props.catalogEntries.length) : props.catalogEntries.length}</strong> {crmOnly ? 'API' : 'APIs'}</span>
            <span><strong>{props.operations.length}</strong> endpoints</span>
            <span><strong>{props.sessionStatus.connected ? props.operations.length : 0}</strong> ready to try</span>
          </div>
          {canAsk && <div className="ai-inline-panel" aria-label="AI assistance for API workbench">
            <div className="ai-inline-copy">
              <Icon name="bot" />
              <span>
                <strong>AI API guide</strong>
                <small>Use chat to explain endpoints, interpret responses, or query related MCP data.</small>
              </span>
            </div>
            <PromptChips prompts={apiPrompts} compact onSelect={props.onAskAi} />
          </div>}
        </div>
      </section>

      {canAsk && <IntegrationKitPanel
        catalogEntries={props.catalogEntries}
        integrationKit={props.integrationKit}
        selectedApiId={props.selectedApiId}
      />}

      <section className="api-overview" aria-label="API catalog">
        <div className="section-heading">
          <div>
            <h2>{crmOnly ? 'CRM API' : 'Discover APIs'}</h2>
            <p>{crmOnly ? 'CRM integration endpoints.' : 'Choose a domain or search across everything.'}</p>
          </div>
          <button className="icon-button" type="button" title="Refresh catalog" aria-label="Refresh catalog" onClick={props.onRefreshCatalog} disabled={props.catalogLoading}>
            <Icon name="refresh-cw" />
          </button>
        </div>
        <ApiCardList
          crmOnly={crmOnly}
          catalogEntries={props.catalogEntries}
          catalogError={props.catalogError}
          catalogLoading={props.catalogLoading}
          operations={props.operations}
          selectedApiId={props.selectedApiId}
          onSelectedApiChange={props.onSelectedApiChange}
        />
      </section>

      <section className="workbench" aria-label="Endpoint workbench">
        <aside className="endpoint-panel" aria-label="Endpoints">
          <div className="section-heading compact">
            <div>
              <h2>Endpoints</h2>
              <p>{getEndpointCountLabel(props.catalogLoading, props.filteredOperations.length)}</p>
            </div>
            <Icon name="list-filter" />
          </div>
          <MethodFilters
            activeMethod={props.activeMethod}
            catalogLoading={props.catalogLoading}
            methodCounts={props.methodCounts}
            onActiveMethodChange={props.onActiveMethodChange}
          />
          <EndpointList
            catalogError={props.catalogError}
            catalogLoading={props.catalogLoading}
            filteredOperations={props.filteredOperations}
            selectedOperationKey={props.selectedOperationKey}
            onSelectedOperationChange={props.onSelectedOperationChange}
          />
        </aside>

        <section className="detail-panel" aria-label="Selected endpoint">
          {!props.selectedOperation && (
            <div className="empty-state">
              <Icon name="sparkles" />
              <h2>Select an endpoint to try it</h2>
              <p>Pick an endpoint from the list to see required inputs, send a request, and inspect the response.</p>
            </div>
          )}

          {props.selectedOperation && (
            <OperationDetail
              activeRequestBodyContentType={props.activeRequestBodyContentType}
              activeTab={props.activeTab}
              copyCurrentRequestUrl={props.copyCurrentRequestUrl}
              copyResponse={props.copyResponse}
              lastResponseText={props.lastResponseText}
              operation={props.selectedOperation}
              parameterValues={props.parameterValues}
              requestBody={props.requestBody}
              requestRunning={props.requestRunning}
              requestUrlCopied={props.requestUrlCopied}
              responseCopied={props.responseCopied}
              responseState={props.responseState}
              sessionStatus={props.sessionStatus}
              tryError={props.tryError}
              onActiveTabChange={props.onActiveTabChange}
              onConnect={props.onConnect}
              onParameterValueChange={props.onParameterValueChange}
              onRequestBodyChange={props.onRequestBodyChange}
              onAskAi={props.onAskAi}
              onSendTryRequest={props.onSendTryRequest}
            />
          )}
        </section>
      </section>
    </section>
  );
}

function IntegrationKitPanel({ catalogEntries, integrationKit, selectedApiId }: {
  catalogEntries: PublicApiCatalogEntry[];
  integrationKit: PublicIntegrationKitSummary | undefined;
  selectedApiId: string;
}): ReactNode {
  if (!integrationKit || catalogEntries.length === 0) {
    return null;
  }

  const visibleEntries = selectedApiId === 'all'
    ? catalogEntries
    : catalogEntries.filter((entry) => entry.id === selectedApiId);

  return (
    <section className="integration-kit" aria-labelledby="integration-kit-title">
      <div className="integration-kit-heading">
        <span className="integration-kit-icon"><Icon name="package" /></span>
        <div>
          <p className="eyebrow">Integration kit</p>
          <h2 id="integration-kit-title">Versioned artifacts, ready for production workflows.</h2>
          <p>Use the same verified contract in documentation, Postman, CI, and generated client code.</p>
        </div>
        <div className="integration-kit-assurance" aria-label="Artifact quality">
          <span><Icon name="shield-check" /> Quality {integrationKit.qualityScore}/100</span>
          <span><Icon name="git-branch" /> Platform {integrationKit.productVersion}</span>
        </div>
      </div>

      <div className="integration-artifact-grid">
        {visibleEntries.map((entry) => (
          <article className="integration-artifact-card" key={entry.id}>
            <div className="integration-artifact-title">
              <span>
                <strong>{entry.title}</strong>
                <small>{entry.protocol === 'mcp' ? 'MCP protocol' : 'REST API'} · {entry.lifecycle}</small>
              </span>
              <em>{entry.contractVersion}</em>
            </div>
            <div className="integration-downloads">
              <ArtifactDownloadLink artifact={entry.artifacts.openApi} label="OpenAPI" />
              <ArtifactDownloadLink artifact={entry.artifacts.postman} label="Postman" />
              {entry.artifacts.sdk && (
                <>
                  <ArtifactDownloadLink artifact={entry.artifacts.sdk.nuget} label="C# NuGet" />
                  <ArtifactDownloadLink artifact={entry.artifacts.sdk.source} label="Source" secondary />
                </>
              )}
            </div>
            {!entry.artifacts.sdk && entry.artifacts.sdkGuidance && (
              <p className="integration-sdk-guidance">{entry.artifacts.sdkGuidance}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function ArtifactDownloadLink({ artifact, label, secondary = false }: {
  artifact: PublicIntegrationArtifact;
  label: string;
  secondary?: boolean;
}): ReactNode {
  return (
    <a
      className={classNames('artifact-download', secondary && 'secondary')}
      href={artifact.url}
      download={artifact.fileName}
      title={`${artifact.fileName} · SHA-256 ${artifact.sha256}`}
    >
      <Icon name="download" />
      <span>
        <strong>{label}</strong>
        <small>{formatArtifactSize(artifact.bytes)} · {artifact.sha256.slice(0, 8)}</small>
      </span>
    </a>
  );
}

function ApiCardList({ crmOnly = false, catalogEntries, catalogError, catalogLoading, operations, selectedApiId, onSelectedApiChange }: {
  crmOnly?: boolean;
  catalogEntries: PublicApiCatalogEntry[];
  catalogError: string;
  catalogLoading: boolean;
  operations: ApiOperation[];
  selectedApiId: string;
  onSelectedApiChange(apiId: string): void;
}): ReactNode {
  const apiCards = useMemo(() => {
    if (crmOnly) {
      return [{ id: 'all', title: 'CRM API', description: 'CRM integration endpoints.', count: operations.length }];
    }
    const operationCounts = new Map<string, number>();
    for (const operation of operations) {
      operationCounts.set(operation.apiId, (operationCounts.get(operation.apiId) ?? 0) + 1);
    }

    return [
      { id: 'all', title: 'All APIs', description: 'Search across every published endpoint.', count: operations.length },
      ...catalogEntries.map((entry) => ({
        id: entry.id,
        title: entry.title,
        description: entry.description ?? 'Published API endpoints.',
        count: operationCounts.get(entry.id) ?? 0
      }))
    ];
  }, [crmOnly, catalogEntries, operations]);

  if (catalogLoading) {
    return <div className="api-card-list"><div className="loading-block">Loading APIs</div></div>;
  }

  if (catalogError) {
    return <div className="api-card-list"><div className="empty-list"><Icon name="life-buoy" /><strong>Catalog unavailable</strong><span>{catalogError}</span></div></div>;
  }

  return (
    <div className="api-card-list">
      {apiCards.map((card) => (
        <ApiCardButton
          card={card}
          key={card.id}
          selected={card.id === selectedApiId}
          onSelect={onSelectedApiChange}
        />
      ))}
    </div>
  );
}

const ApiCardButton = memo(function ApiCardButton({ card, selected, onSelect }: {
  card: { id: string; title: string; description: string; count: number };
  selected: boolean;
  onSelect(apiId: string): void;
}): ReactNode {
  const selectCard = useCallback(() => onSelect(card.id), [card.id, onSelect]);

  return (
    <button
      className={classNames('api-card', selected && 'selected')}
      type="button"
      onClick={selectCard}
    >
      <span className="api-card-icon"><Icon name={getApiCardIcon(card.id)} /></span>
      <span className="api-card-copy">
        <strong>{card.title}</strong>
        <span>{card.description}</span>
      </span>
      <span className="api-card-footer">
        <span>{card.count} endpoints</span>
        <em>View endpoints</em>
      </span>
    </button>
  );
});

function MethodFilters({ activeMethod, catalogLoading, methodCounts, onActiveMethodChange }: {
  activeMethod: string;
  catalogLoading: boolean;
  methodCounts: Record<string, number>;
  onActiveMethodChange(method: string): void;
}): ReactNode {
  if (catalogLoading) {
    return <div className="method-filters" aria-label="Filter by method" />;
  }

  return (
    <div className="method-filters" aria-label="Filter by method">
      {['all', ...HTTP_METHODS].map((method) => (
        <button
          key={method}
          className={classNames('filter-chip', method === activeMethod && 'selected')}
          type="button"
          onClick={() => onActiveMethodChange(method)}
        >
          {method === 'all' ? `All ${methodCounts[method] ?? 0}` : `${method} ${methodCounts[method] ?? 0}`}
        </button>
      ))}
    </div>
  );
}

function EndpointList({ catalogError, catalogLoading, filteredOperations, selectedOperationKey, onSelectedOperationChange }: {
  catalogError: string;
  catalogLoading: boolean;
  filteredOperations: ApiOperation[];
  selectedOperationKey: string;
  onSelectedOperationChange(operationKey: string): void;
}): ReactNode {
  if (catalogLoading) {
    return <nav className="endpoint-list"><div className="loading-block">Loading endpoints</div></nav>;
  }

  if (catalogError) {
    return <nav className="endpoint-list"><div className="empty-list"><Icon name="life-buoy" /><strong>Unable to load endpoints</strong><span>{catalogError}</span></div></nav>;
  }

  if (filteredOperations.length === 0) {
    return (
      <nav className="endpoint-list">
        <div className="empty-list">
          <Icon name="search" />
          <strong>No endpoints found</strong>
          <span>Try a different search, API, or method filter.</span>
        </div>
      </nav>
    );
  }

  return (
    <VirtualEndpointList
      filteredOperations={filteredOperations}
      selectedOperationKey={selectedOperationKey}
      onSelectedOperationChange={onSelectedOperationChange}
    />
  );
}

function VirtualEndpointList({ filteredOperations, selectedOperationKey, onSelectedOperationChange }: {
  filteredOperations: ApiOperation[];
  selectedOperationKey: string;
  onSelectedOperationChange(operationKey: string): void;
}): ReactNode {
  const listRef = useRef<HTMLElement>(null);
  const previousOperationsRef = useRef(filteredOperations);
  const [viewport, setViewport] = useState({ height: 640, scrollTop: 0 });

  const updateViewport = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    const next = { height: list.clientHeight, scrollTop: list.scrollTop };
    setViewport((current) => (
      current.height === next.height && current.scrollTop === next.scrollTop ? current : next
    ));
  }, []);

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    let animationFrame = 0;
    const handleScroll = () => {
      if (animationFrame) {
        return;
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        updateViewport();
      });
    };

    updateViewport();
    list.addEventListener('scroll', handleScroll, { passive: true });
    const resizeObserver = new ResizeObserver(updateViewport);
    resizeObserver.observe(list);

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver.disconnect();
      list.removeEventListener('scroll', handleScroll);
    };
  }, [updateViewport]);

  useEffect(() => {
    if (previousOperationsRef.current === filteredOperations) {
      return;
    }

    previousOperationsRef.current = filteredOperations;
    if (listRef.current) {
      listRef.current.scrollTop = 0;
      updateViewport();
    }
  }, [filteredOperations, updateViewport]);

  const totalHeight = filteredOperations.length * ENDPOINT_ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(viewport.scrollTop / ENDPOINT_ROW_HEIGHT) - ENDPOINT_OVERSCAN);
  const endIndex = Math.min(
    filteredOperations.length,
    Math.ceil((viewport.scrollTop + viewport.height) / ENDPOINT_ROW_HEIGHT) + ENDPOINT_OVERSCAN
  );
  const visibleOperations = filteredOperations.slice(startIndex, endIndex);
  const offsetY = startIndex * ENDPOINT_ROW_HEIGHT;

  return (
    <nav ref={listRef} className="endpoint-list">
      <div className="endpoint-virtual-spacer" style={{ height: totalHeight }}>
        <div className="endpoint-virtual-window" style={{ transform: `translateY(${offsetY}px)` }}>
          {visibleOperations.map((operation) => (
            <EndpointRow
              key={operation.key}
              operation={operation}
              selected={operation.key === selectedOperationKey}
              onSelect={onSelectedOperationChange}
            />
          ))}
        </div>
      </div>
    </nav>
  );
}

const EndpointRow = memo(function EndpointRow({ operation, selected, onSelect }: {
  operation: ApiOperation;
  selected: boolean;
  onSelect(operationKey: string): void;
}): ReactNode {
  const selectOperation = useCallback(() => onSelect(operation.key), [onSelect, operation.key]);

  return (
    <button
      className={classNames('endpoint-row', selected && 'selected')}
      type="button"
      onClick={selectOperation}
    >
      <span className={classNames('method-badge', getMethodClass(operation.method))}>{operation.method}</span>
      <span className="endpoint-copy">
        <strong>{operation.summary}</strong>
        <code>{operation.path}</code>
        <span>{operation.apiTitle}</span>
      </span>
      <span className="endpoint-action">Try</span>
    </button>
  );
});

function OperationDetail(props: {
  activeRequestBodyContentType: string;
  activeTab: ActiveTab;
  copyCurrentRequestUrl(): void;
  copyResponse(): void;
  lastResponseText: string;
  operation: ApiOperation;
  parameterValues: Record<string, string>;
  requestBody: string;
  requestRunning: boolean;
  requestUrlCopied: boolean;
  responseCopied: boolean;
  responseState: TryResponseState;
  sessionStatus: SessionStatus;
  tryError: string;
  onActiveTabChange(tab: ActiveTab): void;
  onConnect(): void;
  onParameterValueChange(key: string, value: string): void;
  onRequestBodyChange(value: string): void;
  onAskAi(message: string): void;
  onSendTryRequest(): void;
}): ReactNode {
  const endpointPrompt = `Explain how to use ${props.operation.method} ${props.operation.path} from ${props.operation.apiTitle}. Include required inputs, common failure modes, and any related MCP data I should inspect.`;

  return (
    <div className="operation-detail">
      <div className="operation-hero">
        <div className="operation-title-block">
          <span className={classNames('method-badge', getMethodClass(props.operation.method))}>{props.operation.method}</span>
          <div>
            <h2>{props.operation.summary}</h2>
            <p className="operation-path">{props.operation.path}</p>
          </div>
        </div>
        <div className="operation-meta">
          <span className="meta-pill">{props.operation.apiTitle}</span>
          <span className="tag-list">
            {props.operation.tags.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}
          </span>
        </div>
        <p className="operation-description">{props.operation.description || props.operation.api.description || 'Ready to try through the iasset.com portal proxy.'}</p>
        {props.sessionStatus.isSuperUser && <div className="operation-ai-strip" aria-label="AI endpoint assistance">
          <div>
            <Icon name="sparkles" />
            <span>Ask AI to explain this endpoint or connect it to customer MCP data.</span>
          </div>
          <button className="secondary-button compact-button" type="button" onClick={() => props.onAskAi(endpointPrompt)}>
            <Icon name="bot" />
            <span>Ask AI</span>
          </button>
        </div>}
      </div>

      <div className="tab-list" role="tablist" aria-label="Endpoint tools">
        <button
          className={classNames('tab-button', props.activeTab === 'try' && 'selected')}
          type="button"
          role="tab"
          aria-selected={props.activeTab === 'try'}
          aria-controls="try-tab-panel"
          onClick={() => props.onActiveTabChange('try')}
        >
          <Icon name="send" />
          <span>Try It</span>
        </button>
        <button
          className={classNames('tab-button', props.activeTab === 'reference' && 'selected')}
          type="button"
          role="tab"
          aria-selected={props.activeTab === 'reference'}
          aria-controls="reference-tab-panel"
          onClick={() => props.onActiveTabChange('reference')}
        >
          <Icon name="book-open" />
          <span>Reference</span>
        </button>
      </div>

      {props.activeTab === 'try' && (
        <section id="try-tab-panel" className="tab-panel" role="tabpanel">
          <AuthCallout sessionStatus={props.sessionStatus} onConnect={props.onConnect} />
          <TryForm
            activeRequestBodyContentType={props.activeRequestBodyContentType}
            operation={props.operation}
            parameterValues={props.parameterValues}
            requestBody={props.requestBody}
            requestRunning={props.requestRunning}
            requestUrlCopied={props.requestUrlCopied}
            tryError={props.tryError}
            onCopyCurrentRequestUrl={props.copyCurrentRequestUrl}
            onParameterValueChange={props.onParameterValueChange}
            onRequestBodyChange={props.onRequestBodyChange}
            onSendTryRequest={props.onSendTryRequest}
          />
          <ResponseCard
            copied={props.responseCopied}
            disabled={!props.lastResponseText}
            responseState={props.responseState}
            onCopyResponse={props.copyResponse}
          />
        </section>
      )}

      {props.activeTab === 'reference' && (
        <section id="reference-tab-panel" className="tab-panel" role="tabpanel">
          <ScalarReference operation={props.operation} />
        </section>
      )}
    </div>
  );
}

function AuthCallout({ sessionStatus, onConnect }: {
  sessionStatus: SessionStatus;
  onConnect(): void;
}): ReactNode {
  if (sessionStatus.connected) {
    return (
      <div className="auth-callout connected">
        <Icon name="check-circle-2" />
        <span>Connected{sessionStatus.customerCode ? ` as ${sessionStatus.customerCode}` : ''}. Requests will use the portal token proxy.</span>
      </div>
    );
  }

  return (
    <div className="auth-callout disconnected">
      <Icon name="plug" />
      <span>Connect once, then send requests without handling tokens.</span>
      <button className="secondary-button compact-button" type="button" onClick={onConnect}>Connect</button>
    </div>
  );
}

function TryForm(props: {
  activeRequestBodyContentType: string;
  operation: ApiOperation;
  parameterValues: Record<string, string>;
  requestBody: string;
  requestRunning: boolean;
  requestUrlCopied: boolean;
  tryError: string;
  onCopyCurrentRequestUrl(): void;
  onParameterValueChange(key: string, value: string): void;
  onRequestBodyChange(value: string): void;
  onSendTryRequest(): void;
}): ReactNode {
  const bodyOption = useMemo(() => getPreferredRequestBody(props.operation), [props.operation]);
  const methodAllowsBody = props.operation.method !== 'GET' && props.operation.method !== 'HEAD';
  const showBody = Boolean(bodyOption && methodAllowsBody && props.activeRequestBodyContentType);

  return (
    <form className="try-form" onSubmit={(event) => {
      event.preventDefault();
      props.onSendTryRequest();
    }}>
      <ParameterGroups
        operation={props.operation}
        parameterValues={props.parameterValues}
        onParameterValueChange={props.onParameterValueChange}
      />

      {showBody && (
        <label className="body-field">
          <span>
            Request body
            <small>{props.activeRequestBodyContentType}{bodyOption?.required ? ' required' : ''}</small>
          </span>
          <textarea spellCheck={false} value={props.requestBody} onChange={(event) => props.onRequestBodyChange(event.currentTarget.value)} />
        </label>
      )}

      <p className="form-error" role="alert">{props.tryError}</p>
      <div className="request-actions">
        <button className="primary-button" type="submit" disabled={props.requestRunning}>
          <Icon name="send" />
          <span>Send</span>
        </button>
        <button className="secondary-button" type="button" disabled={props.requestRunning} onClick={props.onCopyCurrentRequestUrl}>
          <Icon name="clipboard" />
          <span>{props.requestUrlCopied ? 'Copied' : 'Copy URL'}</span>
        </button>
      </div>
    </form>
  );
}

function ParameterGroups({ operation, parameterValues, onParameterValueChange }: {
  operation: ApiOperation;
  parameterValues: Record<string, string>;
  onParameterValueChange(key: string, value: string): void;
}): ReactNode {
  const parameters = useMemo(
    () => dedupeParameters(operation.parameters).filter((parameter) => parameter.in !== 'cookie'),
    [operation.parameters]
  );
  const groups = useMemo(() => ([
    {
      location: 'path' as const,
      title: 'Path parameters',
      parameters: parameters
        .filter((parameter) => parameter.in === 'path')
        .sort(compareRequiredParameters)
    },
    {
      location: 'query' as const,
      title: 'Query parameters',
      parameters: parameters
        .filter((parameter) => parameter.in === 'query')
        .sort(compareRequiredParameters)
    },
    {
      location: 'header' as const,
      title: 'Headers',
      parameters: parameters
        .filter((parameter) => parameter.in === 'header')
        .sort(compareRequiredParameters)
    }
  ]), [parameters]);

  if (parameters.length === 0) {
    return (
      <div className="try-parameters">
        <div className="parameter-empty"><strong>No parameters required</strong><span>This endpoint can be sent as-is once connected.</span></div>
      </div>
    );
  }

  return (
    <div className="try-parameters">
      {groups.map((group) => {
        if (group.parameters.length === 0) {
          return null;
        }

        return (
          <section className="parameter-group" key={group.location}>
            <h3>{group.title}</h3>
            {group.parameters.map((parameter) => (
              <ParameterField
                key={parameterKey(parameter)}
                parameter={parameter}
                value={parameterValues[parameterKey(parameter)] ?? ''}
                onChange={(value) => onParameterValueChange(parameterKey(parameter), value)}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

function compareRequiredParameters(left: OpenApiParameter, right: OpenApiParameter): number {
  return Number(Boolean(right.required)) - Number(Boolean(left.required)) || left.name.localeCompare(right.name);
}

function ParameterField({ parameter, value, onChange }: {
  parameter: OpenApiParameter;
  value: string;
  onChange(value: string): void;
}): ReactNode {
  const schemaType = parameter.schema?.type;

  return (
    <label className="try-field">
      <span className="try-field-heading">
        <span>{parameter.name}{parameter.required && <b> required</b>}</span>
        <small>{getSchemaTypeLabel(parameter.schema)}</small>
      </span>
      {schemaType === 'boolean' ? (
        <select required={Boolean(parameter.required)} value={value} onChange={(event) => onChange(event.currentTarget.value)}>
          <option value="" />
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input
          type={schemaType === 'integer' || schemaType === 'number' ? 'number' : 'text'}
          placeholder={parameter.required ? 'Required' : 'Optional'}
          required={Boolean(parameter.required)}
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )}
      <small className="try-field-description">{parameter.description ?? `${parameter.in} parameter`}</small>
    </label>
  );
}

function ResponseCard({ copied, disabled, responseState, onCopyResponse }: {
  copied: boolean;
  disabled: boolean;
  responseState: TryResponseState;
  onCopyResponse(): void;
}): ReactNode {
  return (
    <section className="response-card" aria-label="Response">
      <div className="response-header">
        <div>
          <h3>Response</h3>
          <p>{renderResponseSummary(responseState)}</p>
        </div>
        <button className={classNames('icon-button', copied && 'copied')} type="button" title="Copy response" aria-label="Copy response" disabled={disabled} onClick={onCopyResponse}>
          <Icon name="copy" />
        </button>
      </div>
      <pre className={classNames('response-body', responseState.loading && 'loading')}>{responseState.body}</pre>
      <details className="response-headers">
        <summary>Headers</summary>
        <pre>{responseState.headers}</pre>
      </details>
    </section>
  );
}

function ScalarReference({ operation }: { operation: ApiOperation }): ReactNode {
  const hostRef = useRef<HTMLDivElement>(null);
  const mountedApiId = useRef('');

  useEffect(() => {
    const host = hostRef.current;
    if (!host || mountedApiId.current === operation.apiId) {
      return;
    }

    let cancelled = false;
    mountedApiId.current = operation.apiId;
    host.replaceChildren();

    void Promise.all([
      import('@scalar/api-reference'),
      import('@scalar/api-reference/style.css')
    ]).then(([{ createApiReference }]) => {
      if (cancelled || !hostRef.current) {
        return;
      }

      createApiReference(hostRef.current, {
        url: operation.api.openApiUrl,
        layout: 'modern',
        theme: 'default',
        darkMode: true,
        forceDarkModeState: 'dark',
        hideDarkModeToggle: true,
        hideClientButton: true,
        hideDownloadButton: false,
        hideModels: false,
        showDeveloperTools: 'never',
        metaData: {
          title: `${operation.apiTitle} | iasset.com APIs`
        }
      });
    });

    return () => {
      cancelled = true;
      host.replaceChildren();
    };
  }, [operation.api.openApiUrl, operation.apiId, operation.apiTitle]);

  useEffect(() => () => {
    hostRef.current?.replaceChildren();
  }, []);

  return <div ref={hostRef} className="scalar-host" />;
}

function ChatView({
  chatError,
  chatInput,
  chatMessages,
  chatSending,
  chatProfiles,
  selectedChatProfileId,
  sessionStatus,
  onChatInputChange,
  onChatProfileChange,
  onConnect,
  onResetConversation,
  onRetryChatMessage,
  onSendChatPrompt,
  onSendChatMessage,
  onStopChat
}: {
  chatError: string;
  chatInput: string;
  chatMessages: ChatMessage[];
  chatSending: boolean;
  chatProfiles: PortalChatProfileSummary[];
  selectedChatProfileId: string;
  sessionStatus: SessionStatus;
  onChatInputChange(value: string): void;
  onChatProfileChange(profileId: string): void;
  onConnect(): void;
  onResetConversation(): void;
  onRetryChatMessage(message: string, promptExecution?: CatalogPromptExecution, turnId?: string): void;
  onSendChatPrompt(message: string, prompt?: AiPrompt): void;
  onSendChatMessage(): void;
  onStopChat(): void;
}): ReactNode {
  const transcriptRef = useRef<HTMLElement>(null);
  const chatMessageRefs = useRef(new Map<string, HTMLElement>());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fullscreenDialogRef = useRef<HTMLDialogElement>(null);
  const fullscreenTriggerRef = useRef<HTMLButtonElement>(null);
  const [followingLatest, setFollowingLatest] = useState(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState('');
  const [showInsights, setShowInsights] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const selectedChatProfile = chatProfiles.find((profile) => profile.id === selectedChatProfileId) ?? chatProfiles[0];
  const environmentName = sessionStatus.environment.trim().toUpperCase();

  const sendFromComposer = () => {
    onSendChatMessage();
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleChatInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key !== 'Enter'
      || event.shiftKey
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.nativeEvent.isComposing
    ) {
      return;
    }

    event.preventDefault();
    sendFromComposer();
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    const computedMaxHeight = Number.parseFloat(window.getComputedStyle(textarea).maxHeight);
    const nextHeight = Math.min(textarea.scrollHeight, Number.isFinite(computedMaxHeight) ? computedMaxHeight : textarea.scrollHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > nextHeight ? 'auto' : 'hidden';
  }, [chatInput]);

  useEffect(() => {
    const dialog = fullscreenDialogRef.current;
    if (!isFullscreen || !dialog || dialog.open) {
      return;
    }

    dialog.showModal();
  }, [isFullscreen]);

  useEffect(() => {
    if (chatSending) {
      setFollowingLatest(true);
    }
  }, [chatSending]);

  useEffect(() => {
    if (!followingLatest || !transcriptRef.current) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const transcript = transcriptRef.current;
      if (!transcript) {
        return;
      }

      transcript.scrollTop = transcript.scrollHeight;
      setShowJumpToLatest(false);
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [chatMessages, chatSending, followingLatest]);

  const handleTranscriptScroll = () => {
    const transcript = transcriptRef.current;
    if (!transcript) {
      return;
    }

    const distanceFromBottom = transcript.scrollHeight - transcript.scrollTop - transcript.clientHeight;
    const isNearBottom = distanceFromBottom < 96;
    setFollowingLatest(isNearBottom);
    setShowJumpToLatest(!isNearBottom);
  };

  const jumpToLatest = () => {
    const transcript = transcriptRef.current;
    if (!transcript) {
      return;
    }

    setFollowingLatest(true);
    setShowJumpToLatest(false);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    transcript.scrollTo({ top: transcript.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  const jumpToChatMessage = (messageId: string) => {
    const transcript = transcriptRef.current;
    const target = chatMessageRefs.current.get(messageId);
    if (!transcript || !target) {
      return;
    }

    setFollowingLatest(false);
    setShowJumpToLatest(true);
    const transcriptBounds = transcript.getBoundingClientRect();
    const targetBounds = target.getBoundingClientRect();
    const targetTop = Math.max(0, transcript.scrollTop + targetBounds.top - transcriptBounds.top - 12);
    transcript.scrollTo({ top: targetTop, behavior: 'auto' });
    target.focus({ preventScroll: true });
  };

  const copyAssistantMessage = async (message: ChatMessage) => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopiedMessageId(message.id);
      window.setTimeout(() => setCopiedMessageId((current) => current === message.id ? '' : current), 1200);
    } catch {
      // Clipboard access can be unavailable in locked-down browser contexts.
    }
  };

  const downloadAssistantMessage = (message: ChatMessage) => {
    const url = URL.createObjectURL(new Blob([message.text], { type: 'text/markdown;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `iasset-answer-${new Date().toISOString().slice(0, 10)}.md`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    window.requestAnimationFrame(() => fullscreenTriggerRef.current?.focus());
  };

  const activeAssistant = [...chatMessages].reverse().find((message) => message.role === 'assistant' && message.streaming);
  const liveStatus = activeAssistant
    ? getChatPhaseLabel(activeAssistant)
    : chatError
      ? 'The assistant response needs attention.'
      : '';

  const chatShell = (
    <section className="chat-shell" aria-label="MCP assistant chat">
        <p className="sr-only" aria-live="polite" aria-atomic="true">{liveStatus}</p>
        <header className="chat-header">
          <div className="chat-header-title">
            <span className="chat-header-mark"><Icon name="bot" /></span>
            <div>
              <p className="eyebrow">AI Chat</p>
              <h2>Ask the {environmentName ? `${environmentName} ` : ''}MCP assistant</h2>
            </div>
          </div>
          {chatProfiles.length > 0 && (
            <ChatProfileSelector
              profiles={chatProfiles}
              selectedProfileId={selectedChatProfileId}
              onSelect={onChatProfileChange}
            />
          )}
          <div className="chat-header-actions">
            <span className={classNames('status-pill', sessionStatus.connected ? 'connected' : 'disconnected')}>
              {sessionStatus.connected ? sessionStatus.customerCode ?? 'Connected' : 'Connect required'}
            </span>
            {!sessionStatus.connected && (
              <button className="secondary-button compact-button" type="button" onClick={onConnect}>Connect</button>
            )}
            <button
              className={classNames('icon-button', showInsights && 'selected')}
              type="button"
              title={showInsights ? 'Hide context panel' : 'Show context panel'}
              aria-label={showInsights ? 'Hide context panel' : 'Show context panel'}
              aria-expanded={showInsights}
              aria-controls="chat-context-panel"
              onClick={() => setShowInsights((current) => !current)}
            >
              <Icon name="list-filter" />
            </button>
            {isFullscreen ? (
              <button className="icon-button chat-fullscreen-toggle" type="button" title="Exit full screen" aria-label="Exit full screen chat" onClick={closeFullscreen}>
                <Icon name="minimize" />
              </button>
            ) : (
              <button ref={fullscreenTriggerRef} className="icon-button chat-fullscreen-toggle" type="button" title="Full screen chat" aria-label="Open full screen chat" onClick={() => setIsFullscreen(true)}>
                <Icon name="maximize" />
              </button>
            )}
            <button className="icon-button" type="button" title="Reset conversation" aria-label="Reset conversation" onClick={onResetConversation}>
              <Icon name="refresh-cw" />
            </button>
          </div>
        </header>

        <div className={classNames('chat-workspace', !showInsights && 'insights-hidden')}>
          <div className="chat-transcript-panel">
            <section
              ref={transcriptRef}
              className="chat-transcript"
              aria-label="Conversation transcript"
              aria-busy={chatSending}
              onScroll={handleTranscriptScroll}
            >
              {chatMessages.length === 0 && !chatSending && (
                <div className="chat-empty-state">
                  <Icon name="bot" />
                  <strong>Start with a customer question</strong>
                  <span>Ask for evidence-based analytics, comparisons, rankings, drill-downs, or an executive summary.</span>
                  <PromptChips prompts={AI_PROMPTS.chat} onSelect={onSendChatPrompt} />
                  <PromptLibrary prompts={MCP_PROMPT_LIBRARY} onSelect={onSendChatPrompt} />
                </div>
              )}

              {chatMessages.map((message) => (
                <article
                  key={message.id}
                  ref={(element) => {
                    if (element) {
                      chatMessageRefs.current.set(message.id, element);
                    } else {
                      chatMessageRefs.current.delete(message.id);
                    }
                  }}
                  tabIndex={-1}
                  className={classNames(
                    'chat-message',
                    message.role === 'user' ? 'user' : 'assistant',
                    message.streaming && 'streaming',
                    message.phase && `phase-${message.phase}`
                  )}
                  aria-busy={message.streaming || undefined}
                >
                  <header>
                    <strong>{message.role === 'user' ? 'You' : 'Assistant'}</strong>
                    {message.role === 'user' ? (
                      <span className="chat-sent-status"><Icon name="check-circle-2" /> Sent</span>
                    ) : (
                      <div className="chat-message-meta">
                        {message.phase && (
                          <span className={classNames('chat-phase-pill', message.phase)}>
                            <Icon name={getChatPhaseIcon(message.phase)} />
                            {getChatPhaseLabel(message)}
                          </span>
                        )}
                        {message.profileLabel && <span>{message.profileLabel}</span>}
                        {message.model && <span>Model {message.model}</span>}
                        {getChatSourceCount(message) > 0 && <span>{getChatSourceCount(message)} sources</span>}
                      </div>
                    )}
                  </header>
                  {message.role === 'assistant' && message.streaming && (
                    <ChatLiveActivity message={message} />
                  )}
                  {message.role === 'assistant' && message.text && (
                    <section className="chat-answer" aria-label="Assistant answer">
                      <ChatMarkdown text={message.text} streaming={message.streaming} />
                    </section>
                  )}
                  {message.role === 'user' && <pre>{message.text}</pre>}
                  {message.role === 'assistant' && <ChatResultCards cards={message.resultCards ?? []} />}
                  {message.role === 'assistant' && !message.streaming && message.phase === 'complete' && (
                    <ChatEvidencePanel message={message} />
                  )}
                  {message.role === 'assistant' && message.phase === 'error' && (
                    <section className="chat-response-notice error" role="alert">
                      <Icon name="life-buoy" />
                      <div>
                        <strong>The response was interrupted</strong>
                        <span>{message.error ?? 'The assistant could not complete this response.'}</span>
                      </div>
                      <div className="chat-response-notice-actions">
                        <button className="secondary-button compact-button" type="button" disabled={chatSending} onClick={() => onRetryChatMessage(message.retryMessage ?? '', message.retryPromptExecution, message.retryTurnId)}>
                          <Icon name="refresh-cw" /> {message.retryTurnId ? 'Retry' : 'Run again'}
                        </button>
                        <button className="secondary-link-button" type="button" onClick={onResetConversation}>Start new chat</button>
                      </div>
                    </section>
                  )}
                  {message.role === 'assistant' && message.phase === 'stopped' && (
                    <section className="chat-response-notice stopped">
                      <Icon name="square" />
                      <div>
                        <strong>Response stopped</strong>
                        <span>{message.text ? 'The partial answer has been kept.' : 'No answer text was generated.'}</span>
                      </div>
                      <div className="chat-response-notice-actions">
                        <button className="secondary-button compact-button" type="button" disabled={chatSending} onClick={() => onRetryChatMessage(message.retryMessage ?? '', message.retryPromptExecution, message.retryTurnId)}>
                          <Icon name="refresh-cw" /> Retry
                        </button>
                      </div>
                    </section>
                  )}
                  {message.role === 'assistant' && !message.streaming && message.phase === 'complete' && message.text && (
                    <ChatResponseActions
                      copied={copiedMessageId === message.id}
                      sourceCount={getChatSourceCount(message)}
                      onJumpToAnswer={() => jumpToChatMessage(message.id)}
                      onJumpToQuestion={message.replyToId ? () => jumpToChatMessage(message.replyToId!) : undefined}
                      onCopy={() => void copyAssistantMessage(message)}
                      onDownload={() => downloadAssistantMessage(message)}
                    />
                  )}
                </article>
              ))}
            </section>
            {showJumpToLatest && (
              <button className="chat-jump-latest" type="button" onClick={jumpToLatest}>
                <Icon name="arrow-down" /> Jump to latest
              </button>
            )}
          </div>

          <ChatInsightsPanel
            chatMessages={chatMessages}
            chatProfile={selectedChatProfile}
            hidden={!showInsights}
            sessionStatus={sessionStatus}
            onSendChatPrompt={onSendChatPrompt}
          />
        </div>

        <form className="chat-compose" onSubmit={(event) => {
          event.preventDefault();
          sendFromComposer();
        }}>
          <label htmlFor="chat-input" className="sr-only">Chat message</label>
          <textarea
            ref={textareaRef}
            id="chat-input"
            rows={1}
            maxLength={CHAT_MESSAGE_MAX_LENGTH}
            placeholder={`Ask about contracts, directories, or onboarding data${environmentName ? ` in ${environmentName}` : ''}.`}
            value={chatInput}
            aria-describedby="chat-compose-help chat-character-count"
            onChange={(event) => onChatInputChange(event.currentTarget.value)}
            onKeyDown={handleChatInputKeyDown}
          />
          <div className="chat-compose-actions">
            <div className="chat-compose-help">
              <span id="chat-compose-help" className="sr-only">Enter to send. Shift+Enter for a new line.</span>
              <span id="chat-character-count" className={classNames(chatInput.length >= CHAT_MESSAGE_MAX_LENGTH * 0.8 && 'warning')}>
                {chatInput.length.toLocaleString()} / {CHAT_MESSAGE_MAX_LENGTH.toLocaleString()}
              </span>
            </div>
            {chatSending ? (
              <button className="chat-stop-button" type="button" title="Stop generating" aria-label="Stop generating" onClick={onStopChat}>
                <Icon name="square" />
              </button>
            ) : (
              <button className="primary-button chat-send-button" type="submit" title="Send message" aria-label="Send" disabled={chatInput.trim().length === 0}>
                <Icon name="send" />
              </button>
            )}
          </div>
        </form>
    </section>
  );

  return isFullscreen ? (
    <dialog
      ref={fullscreenDialogRef}
      className="chat-fullscreen-dialog"
      aria-label="Full screen MCP assistant chat"
      onCancel={(event) => {
        event.preventDefault();
        closeFullscreen();
      }}
      onClose={closeFullscreen}
    >
      {chatShell}
    </dialog>
  ) : (
    <section className="portal-view chat-view" aria-label="Agent chat">
      {chatShell}
    </section>
  );
}

function ChatProfileSelector({ profiles, selectedProfileId, onSelect }: {
  profiles: PortalChatProfileSummary[];
  selectedProfileId: string;
  onSelect(profileId: string): void;
}): ReactNode {
  return (
    <div className="chat-profile-selector" role="radiogroup" aria-label="Chat mode">
      {profiles.map((profile) => {
        const selected = profile.id === selectedProfileId;
        return (
          <button
            key={profile.id}
            className={classNames('chat-profile-option', selected && 'selected')}
            type="button"
            role="radio"
            aria-checked={selected}
            title={profile.description}
            onClick={() => onSelect(profile.id)}
          >
            <Icon name={profile.icon} />
            <span>
              <strong>{profile.label}</strong>
              <small>{profile.description}</small>
            </span>
            {profile.badge && <em>{profile.badge}</em>}
          </button>
        );
      })}
    </div>
  );
}

function ChatLiveActivity({ message }: { message: ChatMessage }): ReactNode {
  const tools = message.activityTools ?? [];

  return (
    <section className="chat-live-activity" aria-label="Assistant progress">
      <div className="chat-live-summary">
        <Icon name="loader-2" />
        <span>
          <strong>{getChatPhaseLabel(message)}</strong>
          <small>Live analysis and source activity</small>
        </span>
      </div>
      {message.reasoningStream && (
        <div className="chat-live-reasoning">
          <span>Reasoning summary</span>
          <p>{message.reasoningStream}</p>
        </div>
      )}
      {message.preambleStream && (
        <div className="chat-live-reasoning">
          <span>Plan</span>
          <p>{message.preambleStream}</p>
        </div>
      )}
      {tools.length > 0 && (
        <ol>
          {tools.map((tool) => (
            <li key={tool.id} className={tool.status}>
              <Icon name={tool.status === 'completed' ? 'check-circle-2' : tool.status === 'failed' ? 'x' : 'plug'} />
              <span className="chat-live-tool-name">
                <strong>{tool.name ? formatToolType(getMcpToolMethodName(tool.name)) : 'MCP tool'}</strong>
                {tool.name && <code>{getMcpToolMethodName(tool.name)}</code>}
              </span>
              <em>{tool.status === 'in_progress' ? 'Running' : tool.status === 'completed' ? 'Complete' : 'Failed'}</em>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ChatEvidencePanel({ message }: { message: ChatMessage }): ReactNode {
  const [open, setOpen] = useState(true);
  const preamble = message.preamble?.trim() ?? '';
  const reasoning = (message.thinking ?? message.reasoning)?.trim() ?? '';
  const runs = message.toolRuns ?? [];
  const fallbackItems = message.observedMcpItems ?? [];
  const citations = message.citations ?? [];
  const sourceCount = getChatSourceCount(message);

  if (!preamble && !reasoning && runs.length === 0 && fallbackItems.length === 0 && citations.length === 0) {
    return null;
  }

  return (
    <details
      className="chat-evidence-panel"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        <span className="chat-evidence-title">
          <Icon name="sparkles" />
          <span>
            <strong>Analysis &amp; evidence</strong>
            <small>How this answer was built</small>
          </span>
        </span>
        <span className="chat-evidence-count">
          {sourceCount > 0 ? `${sourceCount} source${sourceCount === 1 ? '' : 's'}` : 'Reasoning summary'}
        </span>
      </summary>
      <div className="chat-evidence-content">
        {preamble && (
          <section className="chat-reasoning-summary" aria-label="Agent plan">
            <span>Plan</span>
            <ChatMarkdown text={preamble} />
          </section>
        )}
        {reasoning && (
          <section className="chat-reasoning-summary" aria-label="Reasoning summary">
            <span>Reasoning summary</span>
            <ChatMarkdown text={reasoning} />
          </section>
        )}
        <ChatToolRuns runs={runs} fallbackItems={fallbackItems} />
        <ChatCitations citations={citations} />
        <p className="chat-reasoning-disclosure">
          Foundry provides visible planning updates, a concise reasoning summary, and tool/citation evidence; private chain-of-thought is not exposed.
        </p>
      </div>
    </details>
  );
}

function ChatResponseActions({ copied, sourceCount, onJumpToAnswer, onJumpToQuestion, onCopy, onDownload }: {
  copied: boolean;
  sourceCount: number;
  onJumpToAnswer(): void;
  onJumpToQuestion?: () => void;
  onCopy(): void;
  onDownload(): void;
}): ReactNode {
  return (
    <footer className="chat-response-actions">
      <span>
        <Icon name="shield-check" />
        {sourceCount > 0
          ? `Grounded with ${sourceCount} source${sourceCount === 1 ? '' : 's'}`
          : 'Answer ready to reuse'}
      </span>
      <div>
        <button type="button" onClick={onJumpToAnswer}>
          <Icon name="arrow-up" />
          Answer start
        </button>
        {onJumpToQuestion && (
          <button type="button" onClick={onJumpToQuestion}>
            <Icon name="corner-up-left" />
            Question
          </button>
        )}
        <button type="button" onClick={onCopy}>
          <Icon name={copied ? 'check-circle-2' : 'copy'} />
          {copied ? 'Copied' : 'Copy answer'}
        </button>
        <button type="button" onClick={onDownload}>
          <Icon name="download" />
          Markdown
        </button>
      </div>
    </footer>
  );
}

function getChatPhaseLabel(message: ChatMessage): string {
  switch (message.phase) {
    case 'connecting':
      return 'Connecting to Foundry';
    case 'working':
      return 'Planning response';
    case 'tool': {
      const activeTool = message.activityTools?.find((tool) => tool.status === 'in_progress');
      return activeTool?.name ? `Running ${formatToolType(activeTool.name)}` : 'Using MCP tools';
    }
    case 'answering':
      return 'Answering';
    case 'complete':
      return message.durationMs ? `Complete · ${formatChatDuration(message.durationMs)}` : 'Complete';
    case 'stopped':
      return 'Stopped';
    case 'error':
      return 'Needs attention';
    default:
      return 'Preparing response';
  }
}

function getChatPhaseIcon(phase: ChatPhase): string {
  if (phase === 'complete') {
    return 'check-circle-2';
  }

  if (phase === 'stopped') {
    return 'square';
  }

  if (phase === 'error') {
    return 'life-buoy';
  }

  return phase === 'tool' ? 'plug' : 'loader-2';
}

function formatChatDuration(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs}ms`;
  }

  return `${(durationMs / 1000).toFixed(durationMs < 10000 ? 1 : 0)}s`;
}

function getChatHttpErrorMessage(status: number, bodyText: string): string {
  let upstreamMessage = '';
  try {
    const payload = JSON.parse(bodyText) as Record<string, unknown>;
    upstreamMessage = typeof payload.error === 'string'
      ? payload.error
      : typeof payload.detail === 'string'
        ? payload.detail
        : '';
  } catch {
    upstreamMessage = bodyText.trim().slice(0, 240);
  }

  if (status === 400) {
    return upstreamMessage || 'This conversation could not be continued. Retry to start from a fresh context.';
  }

  if (status === 409) {
    return upstreamMessage || 'The previous response is still finishing. Wait a moment, then retry.';
  }

  if (status === 429) {
    return 'Foundry is receiving too many requests. Wait briefly, then retry this message.';
  }

  if (status === 503) {
    return upstreamMessage || 'AI Chat is temporarily unavailable in this environment.';
  }

  if (status >= 500) {
    return 'Foundry could not complete the response. Your question has been preserved for retry.';
  }

  return upstreamMessage || `Chat request failed with status ${status}.`;
}

async function fetchPortalChatWithBusyRetry(init: RequestInit): Promise<Response> {
  let resumeFailureAttempt = 0;
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch('/portal/session/chat', init);
    if (await isCurrentChatBusyResponse(response)) {
      await response.body?.cancel();
      const retryDelay = CHAT_BUSY_RETRY_DELAYS_MS[Math.min(attempt, CHAT_BUSY_RETRY_DELAYS_MS.length - 1)];
      await waitForChatRetry(retryDelay, init.signal);
      continue;
    }

    if (!await isRecoverableStoredResponseFailure(response)) {
      return response;
    }

    const recoveryDelay = CHAT_RESUME_RETRY_DELAYS_MS[resumeFailureAttempt];
    if (recoveryDelay === undefined) {
      return response;
    }
    resumeFailureAttempt += 1;
    await response.body?.cancel();
    await waitForChatRetry(recoveryDelay, init.signal);
  }
}

async function isCurrentChatBusyResponse(response: Response): Promise<boolean> {
  if (response.status !== 409) {
    return false;
  }

  try {
    const payload = await response.clone().json() as Record<string, unknown>;
    return payload.code === 'chat_busy'
      || payload.error === 'Wait for the current response in this chat to finish.';
  } catch {
    return false;
  }
}

async function isRecoverableStoredResponseFailure(response: Response): Promise<boolean> {
  if (response.status < 500) {
    return false;
  }
  try {
    const payload = await response.clone().json() as Record<string, unknown>;
    return payload.code === 'chat_resume_failed';
  } catch {
    return false;
  }
}

function isRetryablePortalChatTransportFailure(error: unknown): boolean {
  if (!(error instanceof Error) || error.name === 'AbortError') {
    return false;
  }
  return error instanceof TypeError || error.message === 'Chat stream ended before the response completed.';
}

function waitForChatRetry(delayMs: number, signal?: AbortSignal | null): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(signal?.reason ?? new DOMException('The chat request was aborted.', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function getChatErrorMessage(error: unknown): string {
  if (error instanceof TypeError) {
    return 'The portal could not reach Foundry. Check the connection and retry this message.';
  }

  return error instanceof Error && error.message.trim()
    ? error.message
    : 'The assistant could not complete this response. Retry to send the same question again.';
}

function PromptChips({ prompts, compact = false, onSelect }: {
  prompts: AiPrompt[];
  compact?: boolean;
  onSelect(message: string, prompt?: AiPrompt): void;
}): ReactNode {
  return (
    <div className={classNames('prompt-chip-list', compact && 'compact')}>
      {prompts.map((prompt) => (
        <button key={prompt.label} className="prompt-chip" type="button" onClick={() => onSelect(prompt.message, prompt)}>
          <Icon name={prompt.icon} />
          <span>{prompt.label}</span>
        </button>
      ))}
    </div>
  );
}

function PromptLibrary({ prompts, onSelect }: {
  prompts: CategorizedAiPrompt[];
  onSelect(message: string, prompt?: AiPrompt): void;
}): ReactNode {
  const categories = [...new Set(prompts.map((prompt) => prompt.category))];

  return (
    <details className="prompt-library">
      <summary>Browse all {prompts.length} expert prompts</summary>
      <div className="prompt-library-content">
        {categories.map((category) => (
          <section key={category}>
            <p>{formatPromptCategory(category)}</p>
            <PromptChips prompts={prompts.filter((prompt) => prompt.category === category)} compact onSelect={onSelect} />
          </section>
        ))}
      </div>
    </details>
  );
}

function formatPromptCategory(category: string): string {
  return category
    .split('-')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}

interface PortalChatStreamHandlers {
  onReady(): void;
  onStarted(): void;
  onTool(tool: AgentChatToolActivity): void;
  onSequenceNumber(sequenceNumber: number): void;
  onPreambleDelta(delta: string): void;
  onReasoningDelta(delta: string): void;
  onTextDelta(delta: string): void;
}

async function readPortalChatResponse(
  response: Response,
  handlers: PortalChatStreamHandlers
): Promise<AgentChatResponse> {
  if (!response.headers.get('content-type')?.includes('text/event-stream')) {
    handlers.onReady();
    return await response.json() as AgentChatResponse;
  }

  if (!response.body) {
    throw new Error('Chat stream did not include a response body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completedResponse: AgentChatResponse | undefined;
  let toolDebugSummary: AgentToolDebugSummary | undefined;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const parsed = consumePortalChatFrames(buffer, handlers);
      buffer = parsed.remainder;
      completedResponse = parsed.completedResponse ?? completedResponse;
      toolDebugSummary = parsed.toolDebugSummary ?? toolDebugSummary;
    }

    if (buffer.trim()) {
      const parsed = consumePortalChatFrames(`${buffer}\n\n`, handlers);
      completedResponse = parsed.completedResponse ?? completedResponse;
      toolDebugSummary = parsed.toolDebugSummary ?? toolDebugSummary;
    }
  } finally {
    reader.releaseLock();
  }

  if (!completedResponse) {
    throw new Error('Chat stream ended before the response completed.');
  }

  return toolDebugSummary
    ? {
      ...completedResponse,
      toolRuns: toolDebugSummary.toolRuns,
      resultCards: toolDebugSummary.resultCards
    }
    : completedResponse;
}

function consumePortalChatFrames(
  input: string,
  handlers: PortalChatStreamHandlers
): { remainder: string; completedResponse?: AgentChatResponse; toolDebugSummary?: AgentToolDebugSummary } {
  const frames = input.replace(/\r\n/g, '\n').split('\n\n');
  const remainder = frames.pop() ?? '';
  let completedResponse: AgentChatResponse | undefined;
  let toolDebugSummary: AgentToolDebugSummary | undefined;

  for (const frame of frames) {
    const lines = frame.split('\n');
    const eventName = lines.find((line) => line.startsWith('event:'))?.slice(6).trim();
    const data = lines
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n')
      .trim();
    if (!data) {
      continue;
    }

    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(data) as Record<string, unknown>;
    } catch {
      throw new Error('Chat stream returned an invalid event.');
    }

    if (eventName === 'ready') {
      handlers.onReady();
      continue;
    }

    if (eventName === 'started') {
      handlers.onStarted();
      continue;
    }

    if (eventName === 'tool') {
      const tool = parseChatToolActivity(payload.tool);
      if (!tool) {
        throw new Error('Chat stream returned invalid MCP activity.');
      }

      handlers.onTool(tool);
      if (Number.isSafeInteger(payload.sequenceNumber) && (payload.sequenceNumber as number) >= 0) {
        handlers.onSequenceNumber(payload.sequenceNumber as number);
      }
      continue;
    }

    if (eventName === 'delta' && typeof payload.delta === 'string') {
      handlers.onTextDelta(payload.delta);
      if (Number.isSafeInteger(payload.sequenceNumber) && (payload.sequenceNumber as number) >= 0) {
        handlers.onSequenceNumber(payload.sequenceNumber as number);
      }
      continue;
    }

    if (eventName === 'reasoning' && typeof payload.delta === 'string') {
      handlers.onReasoningDelta(payload.delta);
      if (Number.isSafeInteger(payload.sequenceNumber) && (payload.sequenceNumber as number) >= 0) {
        handlers.onSequenceNumber(payload.sequenceNumber as number);
      }
      continue;
    }

    if (eventName === 'preamble' && typeof payload.delta === 'string') {
      handlers.onPreambleDelta(payload.delta);
      if (Number.isSafeInteger(payload.sequenceNumber) && (payload.sequenceNumber as number) >= 0) {
        handlers.onSequenceNumber(payload.sequenceNumber as number);
      }
      continue;
    }

    if (eventName === 'tool-debug') {
      if (!Array.isArray(payload.toolRuns) || !Array.isArray(payload.resultCards)) {
        throw new Error('Chat stream returned invalid MCP debug details.');
      }

      toolDebugSummary = payload as unknown as AgentToolDebugSummary;
      continue;
    }

    if (eventName === 'complete') {
      completedResponse = payload as unknown as AgentChatResponse;
      continue;
    }

    if (eventName === 'error') {
      throw new Error(typeof payload.error === 'string' ? payload.error : 'Chat stream failed.');
    }
  }

  return { remainder, completedResponse, toolDebugSummary };
}

function parseChatToolActivity(value: unknown): AgentChatToolActivity | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const tool = value as Record<string, unknown>;
  const status = tool.status;
  if (
    typeof tool.id !== 'string'
    || typeof tool.type !== 'string'
    || (status !== 'in_progress' && status !== 'completed' && status !== 'failed')
  ) {
    return undefined;
  }

  return {
    id: tool.id,
    type: tool.type,
    name: typeof tool.name === 'string' ? tool.name : undefined,
    status
  };
}

function ChatResultCards({ cards }: { cards: AgentResultCard[] }): ReactNode {
  if (cards.length === 0) {
    return null;
  }

  return (
    <section className="chat-result-list" aria-label="Structured MCP results">
      {cards.map((card) => (
        <article key={card.id} className={classNames('chat-result-row', card.tone ?? 'neutral')}>
          <div>
            <strong>{card.title}</strong>
            {card.subtitle && <span>{card.subtitle}</span>}
          </div>
          {card.value && <em>{card.value}</em>}
          {card.meta.length > 0 && (
            <ul>
              {card.meta.map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
        </article>
      ))}
    </section>
  );
}

function ChatToolRuns({ runs, fallbackItems }: { runs: AgentToolRun[]; fallbackItems: string[] }): ReactNode {
  if (runs.length === 0 && fallbackItems.length === 0) {
    return null;
  }

  if (runs.length === 0) {
    return (
      <section className="chat-source-chips" aria-label="MCP activity">
        {fallbackItems.map((item) => {
          const toolName = getObservedMcpToolMethodName(item);
          return (
            <span key={item}>
              <Icon name="plug" />
              <strong>{formatToolType(toolName)}</strong>
              <code>{toolName}</code>
            </span>
          );
        })}
      </section>
    );
  }

  return (
    <section className="chat-tool-runs" aria-label="MCP activity">
      {runs.map((run) => (
        <article key={run.id}>
          <div className="chat-tool-heading">
            <strong>{run.label}</strong>
            <code>{getMcpToolMethodName(run.name)}</code>
            <span>{formatToolType(run.type)}</span>
          </div>
          <em className="chat-tool-status">{formatToolType(run.status ?? 'observed')}</em>
          {(run.resultLabel || run.itemCount) && (
            <p className="chat-tool-outcome">
              {run.resultLabel ?? `${run.itemCount} result${run.itemCount === 1 ? '' : 's'} returned`}
            </p>
          )}
          <ChatToolDetails title="Inputs" details={run.inputs ?? []} />
          <ChatToolDetails title="Result details" details={run.facts ?? []} />
          <ChatToolNotes title="Defaults applied" items={run.defaults ?? []} />
          <ChatToolNotes title="Warnings" items={run.warnings ?? []} warning />
        </article>
      ))}
    </section>
  );
}

function ChatCitations({ citations }: { citations: AgentCitation[] }): ReactNode {
  if (citations.length === 0) return null;
  return (
    <section className="chat-citations" aria-label="External knowledge citations">
      <h4>External knowledge</h4>
      <ul>
        {citations.map((citation) => (
          <li key={citation.id}>
            <Icon name={citation.type === 'url' ? 'globe' : 'file-text'} />
            {citation.url ? (
              <a href={citation.url} target="_blank" rel="noreferrer">{citation.title}</a>
            ) : (
              <span>{citation.title}</span>
            )}
            <small>{citation.type === 'url' ? 'Web source' : 'Curated file snapshot'}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChatToolDetails({ title, details }: { title: string; details: AgentToolDetail[] }): ReactNode {
  if (details.length === 0) {
    return null;
  }

  return (
    <section className="chat-tool-detail-group" aria-label={title}>
      <h4>{title}</h4>
      <dl>
        {details.map((detail) => (
          <div key={`${detail.label}-${detail.value}`}>
            <dt>{detail.label}</dt>
            <dd>{detail.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ChatToolNotes({ title, items, warning = false }: { title: string; items: string[]; warning?: boolean }): ReactNode {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className={classNames('chat-tool-note-group', warning && 'warning')} aria-label={title}>
      <h4>{title}</h4>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </section>
  );
}

function ChatInsightsPanel({ chatMessages, chatProfile, hidden, sessionStatus, onSendChatPrompt }: {
  chatMessages: ChatMessage[];
  chatProfile: PortalChatProfileSummary | undefined;
  hidden: boolean;
  sessionStatus: SessionStatus;
  onSendChatPrompt(message: string, prompt?: AiPrompt): void;
}): ReactNode {
  const latestAssistant = useMemo(() => {
    for (let index = chatMessages.length - 1; index >= 0; index -= 1) {
      if (chatMessages[index].role === 'assistant') {
        return chatMessages[index];
      }
    }

    return undefined;
  }, [chatMessages]);

  const latestRuns = latestAssistant?.toolRuns ?? [];
  const latestCards = latestAssistant?.resultCards ?? [];
  const latestCitations = latestAssistant?.citations ?? [];
  const latestModel = latestAssistant?.model?.trim();

  return (
    <aside
      id="chat-context-panel"
      className="chat-insights-panel"
      aria-label="AI chat context and sources"
      hidden={hidden}
    >
      <header className="chat-insights-header">
        <div>
          <p className="eyebrow">Context</p>
          <strong>{sessionStatus.connected ? sessionStatus.customerCode ?? 'Connected customer' : 'Not connected'}</strong>
        </div>
        {chatProfile && <span>{chatProfile.label}</span>}
      </header>
      <section className="chat-insights-prompts">
        <p className="eyebrow">Suggested prompts</p>
        <PromptChips prompts={AI_PROMPTS.chat} compact onSelect={onSendChatPrompt} />
      </section>
      <section className="chat-insights-activity">
        <div className="chat-insights-section-heading">
          <p className="eyebrow">Latest answer</p>
          {latestModel && <span>{latestModel}</span>}
        </div>
        {latestRuns.length === 0 ? (
          <span className="panel-muted">Tool and external knowledge sources appear after the assistant answers.</span>
        ) : (
          <div className="insight-run-list">
            {latestRuns.slice(0, 4).map((run) => (
              <span key={run.id}>
                <Icon name="plug" />
                <strong>{run.label}</strong>
                <code>{getMcpToolMethodName(run.name)}</code>
                {run.itemCount ? ` (${run.itemCount})` : ''}
              </span>
            ))}
          </div>
        )}
      </section>
      {latestCitations.length > 0 && <ChatCitations citations={latestCitations.slice(0, 6)} />}
      {latestCards.length > 0 && <span className="chat-insights-result-count">{latestCards.length} structured result{latestCards.length === 1 ? '' : 's'}</span>}
    </aside>
  );
}

function getApiPrompts(operation: ApiOperation | undefined): AiPrompt[] {
  if (!operation) {
    return AI_PROMPTS.apis;
  }

  return [
    {
      label: 'Explain selected endpoint',
      message: `Explain ${operation.method} ${operation.path} from ${operation.apiTitle}. Include required inputs, expected response shape, and common errors.`,
      icon: 'book-open'
    },
    {
      label: 'Find related MCP data',
      message: `For ${operation.method} ${operation.path}, identify related MCP data I should query for this customer and summarize what each query would prove.`,
      icon: 'sparkles'
    },
    {
      label: 'Build request input',
      message: `Help me build valid parameters and request body values for ${operation.method} ${operation.path}.`,
      icon: 'send'
    }
  ];
}

function formatToolType(type: string): string {
  return type
    .replace(/[_:.-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Tool';
}

function getMcpToolMethodName(name: string): string {
  const normalized = name.trim();
  return normalized.split(/__|[./]/).filter(Boolean).at(-1) ?? normalized;
}

function getObservedMcpToolMethodName(item: string): string {
  const separatorIndex = item.indexOf(':');
  return getMcpToolMethodName(separatorIndex >= 0 ? item.slice(separatorIndex + 1) : item);
}

function getChatSourceCount(message: ChatMessage): number {
  const toolCount = message.toolRuns?.length || message.observedMcpItems?.length || 0;
  return toolCount + (message.citations?.length ?? 0);
}

function DocsView({ catalogCount, canAsk, docsQuery, selectedSlug, onAskAi, onDocsQueryChange }: {
  catalogCount: number;
  canAsk: boolean;
  docsQuery: string;
  selectedSlug: string;
  onAskAi(message: string): void;
  onDocsQueryChange(query: string): void;
}): ReactNode {
  const page = getDocumentationPage(selectedSlug);

  return (
    <section className="portal-view docs-view" aria-label="Documentation">
      <aside className="docs-sidebar" aria-label="Documentation navigation">
        <div className="section-heading compact">
          <div>
            <h2>Documentation</h2>
            <p>Guides, quickstarts, and operations notes.</p>
          </div>
          <Icon name="book-open" />
        </div>
        <label className="docs-search" htmlFor="docs-search-input">
          <Icon name="search" />
          <input
            id="docs-search-input"
            type="search"
            placeholder="Search docs"
            autoComplete="off"
            value={docsQuery}
            onChange={(event) => onDocsQueryChange(event.currentTarget.value)}
          />
        </label>
        {canAsk && <div className="docs-ai-panel" aria-label="AI documentation help">
          <div>
            <Icon name="bot" />
            <span>
              <strong>Ask AI about docs</strong>
              <small>Turn guidance into customer-specific next steps.</small>
            </span>
          </div>
          <button className="secondary-button compact-button" type="button" onClick={() => onAskAi(`Explain the '${page.title}' documentation page for my customer integration and include the next three actions I should take.`)}>
            <Icon name="sparkles" />
            <span>Ask</span>
          </button>
        </div>}
        <DocsNavigation query={docsQuery} selectedSlug={page.slug} />
      </aside>
      <DocsArticle catalogCount={catalogCount} canAsk={canAsk} page={page} onAskAi={onAskAi} />
    </section>
  );
}

function DocsNavigation({ query, selectedSlug }: { query: string; selectedSlug: string }): ReactNode {
  const deferredQuery = useDeferredValue(query);
  const matchingPages = useMemo(() => {
    const normalizedQuery = deferredQuery.trim().toLowerCase();
    return DOCUMENTATION_PAGES.filter((page) => {
      if (!normalizedQuery) {
        return true;
      }

      return [page.title, page.summary, page.group, page.content].join(' ').toLowerCase().includes(normalizedQuery);
    });
  }, [deferredQuery]);

  const groups = useMemo(() => Array.from(new Set(matchingPages.map((page) => page.group))), [matchingPages]);

  if (matchingPages.length === 0) {
    return (
      <nav className="docs-nav">
        <div className="empty-list"><Icon name="search" /><strong>No docs found</strong><span>Try a different search term.</span></div>
      </nav>
    );
  }

  return (
    <nav className="docs-nav">
      {groups.map((group) => (
        <section className="docs-nav-group" key={group}>
          <h3>{group}</h3>
          {matchingPages.filter((page) => page.group === group).map((page) => (
            <DocsNavigationLink
              key={page.slug}
              page={page}
              selected={page.slug === selectedSlug}
            />
          ))}
        </section>
      ))}
    </nav>
  );
}

const DocsNavigationLink = memo(function DocsNavigationLink({ page, selected }: {
  page: DocumentationPage;
  selected: boolean;
}): ReactNode {
  return (
    <a className={selected ? 'selected' : undefined} href={`#docs/${page.slug}`}>
      <span className="docs-nav-icon" aria-hidden="true"><Icon name={getDocumentationIcon(page)} /></span>
      <span className="docs-nav-copy">
        <strong>{page.title}</strong>
        <span>{page.summary}</span>
      </span>
    </a>
  );
});

function DocsArticle({ catalogCount, canAsk, page, onAskAi }: {
  catalogCount: number;
  canAsk: boolean;
  page: DocumentationPage;
  onAskAi(message: string): void;
}): ReactNode {
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (articleRef.current) {
      articleRef.current.scrollTop = 0;
    }
  }, [page.slug]);

  return (
    <article ref={articleRef} className="docs-article">
      {page.slug === 'home' ? (
        <DocumentationLanding catalogCount={catalogCount} canAsk={canAsk} page={page} onAskAi={onAskAi} />
      ) : (
        <>
          <header className="docs-article-header">
            <span>{page.group}</span>
            <h1>{page.title}</h1>
            <p>{page.summary}</p>
          </header>
          {canAsk && <DocAiCallout page={page} onAskAi={onAskAi} />}
          <MarkdownContent className="docs-content" page={page} />
        </>
      )}
    </article>
  );
}

function DocumentationLanding({ catalogCount, canAsk, page, onAskAi }: {
  catalogCount: number;
  canAsk: boolean;
  page: DocumentationPage;
  onAskAi(message: string): void;
}): ReactNode {
  const startPages = useMemo(() => DOCUMENTATION_PAGES.filter((candidate) => candidate.group === 'Start' && candidate.slug !== 'home').slice(0, 2), []);
  const quickstartPages = useMemo(() => DOCUMENTATION_PAGES.filter((candidate) => candidate.group === 'Quickstarts').slice(0, 4), []);
  const operationsPages = useMemo(() => DOCUMENTATION_PAGES.filter((candidate) => candidate.group === 'Operations').slice(0, 3), []);

  return (
    <>
      <header className="docs-landing-hero">
        <span className="docs-landing-eyebrow">Documentation Hub</span>
        <h1>Get from onboarding to production faster.</h1>
        <p>Use guided steps, API-specific quickstarts, and operational playbooks from one focused documentation experience.</p>
        <div className="docs-landing-actions">
          <a className="primary-link-button" href="#docs/onboarding">
            <Icon name="shield-check" />
            <span>Get Started</span>
          </a>
          <a className="secondary-link-button" href="#apis">
            <Icon name="send" />
            <span>Open API Workbench</span>
          </a>
          {canAsk && <button className="secondary-link-button" type="button" onClick={() => onAskAi('Guide me through onboarding, authentication, first API calls, and the MCP chat workflows I should try next.')}>
            <Icon name="bot" />
            <span>Ask AI</span>
          </button>}
        </div>
        {canAsk && <PromptChips prompts={AI_PROMPTS.docs} compact onSelect={onAskAi} />}
        <div className="docs-landing-stats" aria-label="Documentation highlights">
          <span><strong>{DOCUMENTATION_PAGES.length}</strong> curated guides</span>
          <span><strong>{quickstartPages.length}</strong> API quickstarts</span>
          <span><strong>{catalogCount}</strong> published APIs</span>
        </div>
      </header>

      <section className="docs-landing-grid" aria-label="Start paths">
        <div className="docs-landing-section">
          <h2>Start here</h2>
          <p>Follow these first to connect, authenticate, and send your first successful call.</p>
          <div className="docs-feature-grid">
            {startPages.map((entry) => <DocsFeatureCard key={entry.slug} kicker="Start" page={entry} />)}
          </div>
        </div>

        <aside className="docs-landing-aside">
          <h3>Operations and support</h3>
          <p>Keep these nearby for production readiness, regional design, and incident response.</p>
          <div className="docs-landing-links">
            {operationsPages.map((entry) => (
              <a href={`#docs/${entry.slug}`} key={entry.slug}>
                <span className="docs-nav-icon" aria-hidden="true"><Icon name={getDocumentationIcon(entry)} /></span>
                <span className="docs-nav-copy">
                  <strong>{entry.title}</strong>
                  <span>{entry.summary}</span>
                </span>
              </a>
            ))}
          </div>
        </aside>
      </section>

      <section className="docs-landing-section" aria-label="API quickstarts">
        <h2>API quickstarts</h2>
        <p>Pick a domain and move from credentials to first response quickly.</p>
        <div className="docs-feature-grid">
          {quickstartPages.map((entry) => <DocsFeatureCard key={entry.slug} kicker="Quickstart" page={entry} />)}
        </div>
      </section>

      <MarkdownContent className="docs-content docs-home-content" page={page} />
    </>
  );
}

function DocAiCallout({ page, onAskAi }: { page: DocumentationPage; onAskAi(message: string): void }): ReactNode {
  return (
    <section className="doc-ai-callout" aria-label="AI help for this documentation page">
      <div>
        <Icon name="sparkles" />
        <span>
          <strong>Ask AI about this page</strong>
          <small>Get a customer-specific explanation, checklist, or troubleshooting path.</small>
        </span>
      </div>
      <PromptChips
        compact
        prompts={[
          {
            label: 'Summarize',
            message: `Summarize '${page.title}' for my customer integration and call out the most important decisions.`,
            icon: 'book-open'
          },
          {
            label: 'Next actions',
            message: `Turn '${page.title}' into an action checklist for my customer setup.`,
            icon: 'check-circle-2'
          }
        ]}
        onSelect={onAskAi}
      />
    </section>
  );
}

function DocsFeatureCard({ kicker, page }: { kicker: string; page: DocumentationPage }): ReactNode {
  return (
    <a className="docs-feature-card" href={`#docs/${page.slug}`}>
      <span className="docs-feature-icon" aria-hidden="true"><Icon name={getDocumentationIcon(page)} /></span>
      <span className="docs-feature-kicker">{kicker}</span>
      <strong>{page.title}</strong>
      <span>{page.summary}</span>
      <em>Read guide</em>
    </a>
  );
}

function MarkdownContent({ className, page }: { className: string; page: DocumentationPage }): ReactNode {
  const html = getCachedMarkdownHtml(page);

  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

function getCachedMarkdownHtml(page: DocumentationPage): string {
  const cached = markdownHtmlCache.get(page);
  if (cached !== undefined) {
    return cached;
  }

  const html = renderMarkdown(page.content, { resolveHref: (href) => resolveDocumentationHref(href, page) });
  markdownHtmlCache.set(page, html);
  return html;
}

function ConnectDialog({ error, open, submitting, onClose, onSubmit }: {
  error: string;
  open: boolean;
  submitting: boolean;
  onClose(): void;
  onSubmit(formData: FormData): void;
}): ReactNode {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formRef.current;
    if (form) {
      onSubmit(new FormData(form));
      const passwordInput = form.elements.namedItem('password');
      if (passwordInput instanceof HTMLInputElement) passwordInput.value = '';
    }
  };

  return (
    <dialog ref={dialogRef} className="connect-dialog" onCancel={onClose} onClose={onClose}>
      <form ref={formRef} className="connect-form" aria-busy={submitting} onSubmit={submit}>
        <div className="dialog-header">
          <h2>Connect</h2>
          <button className="icon-button" type="button" title="Close" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <ConnectionFields
          error={error}
        />
        <div className="dialog-actions">
          <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
          <button className="primary-button connect-submit-button" type="submit" disabled={submitting} aria-live="polite">
            <Icon name={submitting ? 'loader-2' : 'shield-check'} />
            <span>{submitting ? 'Connecting' : 'Connect'}</span>
          </button>
        </div>
      </form>
    </dialog>
  );
}

function ConnectionFields({ error, autoFocusFirst = false }: {
  error: string;
  autoFocusFirst?: boolean;
}): ReactNode {
  return (
    <>
      <label>
        <span>Customer code</span>
        <input name="customerCode" type="text" autoComplete="organization" required maxLength={128} autoCapitalize="none" spellCheck={false} autoFocus={autoFocusFirst} />
      </label>
      <label>
        <span>Username</span>
        <input name="username" type="text" autoComplete="username" required />
      </label>
      <label>
        <span>Password</span>
        <input name="password" type="password" autoComplete="current-password" required />
      </label>
      <p className="form-error" role="alert">{error}</p>
    </>
  );
}

function Icon({ name }: { name: string }): ReactNode {
  const Component = ICONS[name] ?? FileText;
  return <Component aria-hidden="true" />;
}

function parseRoute(): PortalRoute {
  const rawHash = window.location.hash.replace(/^#/, '');
  const [rawPath, rawQuery = ''] = rawHash.split('?', 2);
  const path = decodeURIComponent(rawPath);
  const query = new URLSearchParams(rawQuery);
  if (path === 'apis') {
    return {
      view: 'apis',
      documentationSlug: 'home',
      apiId: query.get('api') ?? undefined,
      operationId: query.get('operation') ?? undefined
    };
  }

  if (path === 'chat') {
    return { view: 'chat', documentationSlug: 'home' };
  }

  if (path === 'mcp-tools') {
    return { view: 'mcp-tools', documentationSlug: 'home' };
  }

  if (path === 'jobs') {
    return { view: 'jobs', documentationSlug: 'home' };
  }

  if (path === 'activity') {
    return { view: 'activity', documentationSlug: 'home' };
  }

  if (path.startsWith('docs/')) {
    return { view: 'docs', documentationSlug: path.slice('docs/'.length) || 'home' };
  }

  return { view: 'overview', documentationSlug: 'home' };
}

function getActiveNavigationKey(route: PortalRoute): string {
  if (route.view === 'overview' || route.view === 'apis' || route.view === 'mcp-tools' || route.view === 'jobs' || route.view === 'activity' || route.view === 'chat') {
    return route.view;
  }

  if (route.documentationSlug === 'regional-guidance-sla-rate-limits') {
    return 'environments';
  }

  if (route.documentationSlug === 'troubleshooting') {
    return 'support';
  }

  return 'docs';
}

function getRouteScrollKey(route: PortalRoute): string {
  if (route.view === 'docs') return `${route.view}:${route.documentationSlug}`;
  if (route.view === 'apis') return `${route.view}:${route.apiId ?? ''}:${route.operationId ?? ''}`;
  return route.view;
}

function saveRouteScrollPosition(route: PortalRoute): void {
  const scrollingElement = document.scrollingElement;
  if (!scrollingElement) return;
  window.sessionStorage.setItem(`${ROUTE_SCROLL_STORAGE_PREFIX}${getRouteScrollKey(route)}`, String(scrollingElement.scrollTop));
}

function readRouteScrollPosition(route: PortalRoute): number {
  const value = Number(window.sessionStorage.getItem(`${ROUTE_SCROLL_STORAGE_PREFIX}${getRouteScrollKey(route)}`));
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function getApiCardIcon(apiId: string): string {
  if (apiId === 'all') {
    return 'gauge';
  }

  if (apiId.includes('users')) {
    return 'users';
  }

  if (apiId.includes('directory')) {
    return 'building';
  }

  if (apiId.includes('pipeline')) {
    return 'git-branch';
  }

  if (apiId.includes('agreements')) {
    return 'file-text';
  }

  if (apiId.includes('customer-assets')) {
    return 'package';
  }

  if (apiId.includes('mcp')) {
    return 'bot';
  }

  return 'book-open';
}

function getDocumentationIcon(page: DocumentationPage): string {
  if (page.slug === 'home') {
    return 'book-open';
  }

  if (page.slug === 'onboarding') {
    return 'shield-check';
  }

  if (page.group === 'Quickstarts') {
    return 'rocket';
  }

  if (page.slug === 'regional-guidance-sla-rate-limits') {
    return 'globe';
  }

  if (page.slug === 'troubleshooting') {
    return 'life-buoy';
  }

  if (page.slug === 'mcp-vs-direct-rest') {
    return 'gauge';
  }

  return 'file-text';
}

function createInitialParameterValues(operation: ApiOperation): Record<string, string> {
  const values: Record<string, string> = {};
  for (const parameter of dedupeParameters(operation.parameters).filter((candidate) => candidate.in !== 'cookie')) {
    values[parameterKey(parameter)] = getParameterExample(parameter);
  }

  return values;
}

function buildTryRequest(
  operation: ApiOperation,
  parameterValues: Record<string, string>,
  activeRequestBodyContentType: string,
  requestBodyText: string,
  includeBody = true
): BuiltTryRequest {
  const pathParameters = new Map<string, string>();
  const queryParameters = new URLSearchParams();
  const headers: Record<string, string> = {
    Accept: 'application/json, text/plain;q=0.9, */*;q=0.8'
  };
  if (operation.operationId) {
    headers['X-IAsset-Portal-Operation-ID'] = operation.operationId;
  }
  headers['X-IAsset-Portal-Operation-Path'] = operation.path;
  const missingParameters: string[] = [];
  const parameters = dedupeParameters(operation.parameters).filter((parameter) => parameter.in !== 'cookie');

  for (const parameter of parameters) {
    const value = (parameterValues[parameterKey(parameter)] ?? '').trim();
    if (parameter.required && !value) {
      missingParameters.push(`${parameter.name} (${parameter.in})`);
    }

    if (!value) {
      continue;
    }

    if (parameter.in === 'path') {
      pathParameters.set(parameter.name, value);
    } else if (parameter.in === 'query') {
      queryParameters.append(parameter.name, value);
    } else if (parameter.in === 'header') {
      headers[parameter.name] = value;
    }
  }

  const targetPath = operation.path.replace(/\{([^}]+)\}/g, (_match, parameterName: string) => {
    const value = pathParameters.get(parameterName);
    if (!value) {
      missingParameters.push(`${parameterName} (path)`);
      return '';
    }

    return encodeURIComponent(value);
  });

  if (missingParameters.length > 0) {
    throw new Error(`Fill required fields: ${Array.from(new Set(missingParameters)).join(', ')}.`);
  }

  let body: string | undefined;
  if (includeBody && activeRequestBodyContentType && operation.method !== 'GET' && operation.method !== 'HEAD') {
    const trimmedBody = requestBodyText.trim();
    if (operation.requestBody?.required && !trimmedBody) {
      throw new Error('Request body is required.');
    }

    if (trimmedBody) {
      if (activeRequestBodyContentType.toLowerCase().includes('json')) {
        JSON.parse(trimmedBody);
      }

      headers['Content-Type'] = activeRequestBodyContentType;
      body = trimmedBody;
    }
  }

  const proxyPath = `${operation.api.proxyBaseUrl}${targetPath.startsWith('/') ? targetPath : `/${targetPath}`}`;
  const queryString = queryParameters.toString();
  const url = queryString ? `${proxyPath}?${queryString}` : proxyPath;

  return {
    url,
    absoluteUrl: new URL(url, window.location.origin).toString(),
    init: {
      method: operation.method,
      headers,
      body,
      credentials: 'same-origin'
    }
  };
}

function dedupeParameters(parameters: OpenApiParameter[]): OpenApiParameter[] {
  const deduped = new Map<string, OpenApiParameter>();
  for (const parameter of parameters) {
    deduped.set(parameterKey(parameter), parameter);
  }

  return Array.from(deduped.values());
}

function parameterKey(parameter: OpenApiParameter): string {
  return `${parameter.in}:${parameter.name}`;
}

function formatArtifactSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(bytes >= 10240 ? 0 : 1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getEndpointCountLabel(catalogLoading: boolean, count: number): string {
  if (catalogLoading) {
    return 'Loading endpoints';
  }

  return `${count} ${count === 1 ? 'endpoint' : 'endpoints'}`;
}

function getMethodClass(method: string): string {
  return `method-${method.toLowerCase()}`;
}

function renderResponseSummary(responseState: TryResponseState): ReactNode {
  if (responseState.status !== undefined) {
    return (
      <>
        <span className={classNames('response-status', responseState.ok ? 'success' : 'error')}>
          {responseState.status} {responseState.statusText}
        </span>
        <span>{responseState.duration} ms</span>
      </>
    );
  }

  return responseState.summary;
}

function formatResponseBody(text: string, contentType: string | null): string {
  if (!text) {
    return 'No content.';
  }

  if (contentType?.toLowerCase().includes('json')) {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }

  return text;
}

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element.');
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
